/**
 * Widget contra a API pública `/api/widget/*` — inscrição + checkout transparente Pagar.me (PIX/cartão/boleto).
 */

// Carimbo da versão do bundle, injetado no build pelo Vite (vite.widget.config.ts).
// Em dev (sem build) cai no fallback 'dev'.
declare const __WIDGET_VERSION__: string;
import {
    appendWidgetGeoToPayload,
    createPagarmeCardToken,
    enqueueWidgetEnrollmentIntegration,
    fetchBootstrap,
    fetchCep,
    fetchCities,
    fetchCpfInfo,
    fetchFormTheme,
    fetchGeoJsGeo,
    fetchOfferLegacy,
    fetchStates,
    getWidgetPaymentStatus,
    joinApiBase,
    postEnrollment,
    postEnrollmentPaymentPending,
    simulateWidgetPayment,
    postEnrollmentProgress,
    postWidgetEvent,
    postWidgetPayment,
    validateWidgetCoupon,
    type ApiOfferResponse,
    type WidgetEnrollmentProgressStage,
} from './widgetApi';
import {
    buildPlansFromCombinations,
    calcParcelamento,
    computeScholarshipDiscount,
    escapeHtmlAttr,
    formatPrice,
    getPlanNetPrice,
    installGtm,
    isValidCpf,
    maskCep,
    maskCpf,
    maskTelefone,
    parsePrice,
    pushDataLayer,
    STEP_NAMES,
    STEP_SUBTITLES,
    STEP_TOTAL,
    STEPS,
    type PaymentPlanRow,
} from './widgetHelpers';
import type { WidgetTheme } from './widgetStyles';
import { getThemeVarsCss, WIDGET_STYLES } from './widgetStyles';
import type { DecolaWidgetInitOptions } from './widgetTypes';

/** Tempo mínimo na tela do PIX antes de exibir a confirmação animada (UX). */
const PIX_MIN_MS_BEFORE_CONFIRM_UI = 10_000;
/** Duração da animação "Recebemos o pagamento" antes da tela final de sucesso. */
const PIX_RECEIVED_ANIMATION_MS = 2_500;
/** Copia-e-cola exibido no checkout simulado — não é um PIX válido, e diz isso. */
const SIMULATED_PIX_CODE = '00020126PAGAMENTO-SIMULADO-HOMOLOGACAO-NAO-E-UM-PIX-VALIDO5204000053039865802BR6009SIMULADO';

/**
 * Exibe modal bloqueante quando a versão do widget.js carregado pelo navegador
 * (cache antigo) é diferente da publicada no servidor. CSS é embutido para não
 * depender do bundle de estilos — o objetivo é renderizar sempre, mesmo se algo
 * mais quebrou. O botão "Atualizar agora" faz reload completo da página.
 */
function renderUpdateRequiredModal(shadow: ShadowRoot): void {
    shadow.innerHTML = `
      <style>
        :host { all: initial; }
        .update-overlay {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 320px;
          padding: 2rem 1rem;
          background: #213F9A;
          color: #fff;
          border-radius: 12px;
        }
        .update-card {
          max-width: 360px;
          text-align: center;
        }
        .update-icon {
          width: 56px;
          height: 56px;
          margin: 0 auto 1rem;
          border-radius: 50%;
          background: rgba(255,255,255,0.12);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.75rem;
        }
        .update-title {
          font-size: 1.1rem;
          font-weight: 700;
          margin: 0 0 0.5rem;
        }
        .update-body {
          font-size: 0.92rem;
          line-height: 1.5;
          opacity: 0.92;
          margin: 0 0 1.5rem;
        }
        .update-btn {
          background: #FD4900;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 0.85rem 1.5rem;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
          width: 100%;
          max-width: 240px;
        }
        .update-btn:hover { filter: brightness(0.95); }
      </style>
      <div class="update-overlay" role="alertdialog" aria-modal="true" aria-labelledby="upd-title">
        <div class="update-card">
          <div class="update-icon" aria-hidden="true">↻</div>
          <p id="upd-title" class="update-title">Nova versão disponível</p>
          <p class="update-body">Atualize a página para continuar sua inscrição com a versão mais recente do checkout.</p>
          <button type="button" class="update-btn" id="btn-widget-reload">Atualizar agora</button>
        </div>
      </div>`;
    const btn = shadow.getElementById('btn-widget-reload');
    if (btn) {
        btn.addEventListener('click', () => {
            if (typeof window !== 'undefined') {
                window.location.reload();
            }
        });
    }
}

export type ProductionWidgetOptions = DecolaWidgetInitOptions;

type FormDataState = Record<string, unknown>;

function validateDadosPessoais(formData: FormDataState): Record<string, string> {
    const err: Record<string, string> = {};
    if (formData.estrangeiro) {
        const passaporte = String(formData.passaporte || '').trim();
        if (!passaporte) err.passaporte = 'Passaporte é obrigatório.';
    } else {
        const cpfDigits = String(formData.cpf || '').replace(/\D/g, '');
        if (cpfDigits.length !== 11) err.cpf = 'CPF inválido. Informe os 11 dígitos.';
        else if (!isValidCpf(String(formData.cpf))) err.cpf = 'CPF inválido.';
    }
    const nome = String(formData.nome || '').trim();
    if (!nome) err.nome = 'Nome é obrigatório.';
    else {
        if (!/^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF\s]+$/.test(nome)) {
            err.nome = 'Nome deve conter apenas letras (sem números ou caracteres especiais).';
        } else {
            const partes = nome.split(/\s+/).filter((p) => p.length > 0);
            if (partes.length < 2) err.nome = 'Informe nome e sobrenome.';
        }
    }
    const email = String(formData.email || '').trim();
    if (!email) err.email = 'E-mail é obrigatório.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) err.email = 'Informe um e-mail válido.';
    const telDigits = String(formData.telefone || '').replace(/\D/g, '');
    if (telDigits.length === 0) err.telefone = 'Telefone é obrigatório.';
    else if (telDigits.length < 10 || telDigits.length > 11) {
        err.telefone = 'Telefone inválido. Informe DDD + número (10 ou 11 dígitos).';
    }
    return err;
}

function validateEnderecoProd(formData: FormDataState): Record<string, string> {
    const err: Record<string, string> = {};
    const cepDigits = String(formData.cep || '').replace(/\D/g, '');
    if (cepDigits.length !== 8) err.cep = 'CEP inválido. Informe os 8 dígitos.';
    const endereco = String(formData.endereco || '').trim();
    if (!endereco) err.endereco = 'Endereço é obrigatório.';
    else if (endereco.length > 100) err.endereco = 'Endereço deve ter no máximo 100 caracteres.';
    const numero = String(formData.numero || '').trim();
    if (!numero) err.numero = 'Número é obrigatório.';
    else if (numero.length > 8) err.numero = 'Número deve ter no máximo 8 caracteres.';
    const complemento = String(formData.complemento || '').trim();
    if (complemento.length > 60) err.complemento = 'Complemento deve ter no máximo 60 caracteres.';
    const bairro = String(formData.bairro || '').trim();
    if (!bairro) err.bairro = 'Bairro é obrigatório.';
    else if (bairro.length > 80) err.bairro = 'Bairro deve ter no máximo 80 caracteres.';
    if (!Number(formData.state_id)) err.estado = 'Selecione o estado.';
    if (!Number(formData.city_id)) err.cidade = 'Selecione a cidade.';
    return err;
}

function admissionTypeIdFromKey(key: string): number | null {
    const m = /^at_(\d+)$/.exec(String(key));
    return m ? parseInt(m[1], 10) : null;
}

function generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

const SESSION_STORAGE_KEY = 'decolaedu.widget.session';
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Session id do checkout, persistido por 24h. Sem persistir, cada recarregamento
 * da landing page gerava um id novo e o backend abria outra inscrição para o
 * mesmo aluno — com uma cobrança PIX/boleto paralela em cada uma. O TTL evita
 * que uma visita muito posterior reaproveite um checkout esquecido.
 *
 * O backend ainda deduplica por CPF + e-mail (Enrollment::resolveWidgetDraft),
 * o que cobre outro dispositivo e o caso do localStorage indisponível.
 */
function resolveSessionId(): string {
    try {
        const [id, expiresAt] = String(localStorage.getItem(SESSION_STORAGE_KEY) ?? '').split('|');
        if (id && Number(expiresAt) > Date.now()) return id;
    } catch {
        /* localStorage bloqueado (modo privado / cookies de terceiros) */
    }

    const id = generateSessionId();
    try {
        localStorage.setItem(SESSION_STORAGE_KEY, `${id}|${Date.now() + SESSION_TTL_MS}`);
    } catch {
        /* segue apenas em memória, como antes */
    }
    return id;
}

async function copyTextToClipboard(text: string): Promise<boolean> {
    const t = String(text);
    if (t === '') return false;
    try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(t);
            return true;
        }
    } catch {
        /* fallback abaixo */
    }
    try {
        const ta = document.createElement('textarea');
        ta.value = t;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch {
        return false;
    }
}

function flashButtonLabel(btn: HTMLButtonElement, doneLabel: string, ms = 2000): void {
    const prev = btn.textContent ?? '';
    btn.textContent = doneLabel;
    btn.disabled = true;
    window.setTimeout(() => {
        btn.textContent = prev;
        btn.disabled = false;
    }, ms);
}

export async function createProductionWidget(container: string | HTMLElement, options: ProductionWidgetOptions): Promise<void> {
    const root = typeof container === 'string' ? document.querySelector(container) : container;
    if (!root) {
        console.error('[DecolaWidget] Container não encontrado:', container);
        return;
    }

    const apiBase = options.apiBaseUrl ?? '';
    if (!apiBase) {
        console.error(
            '[DecolaWidget] Base da API não definida. Use um URL absoluto no atributo src do script (https://…/assets/js/widget.js) ou defina VITE_APP_URL no build.',
        );
        const shadowErr = root.attachShadow({ mode: 'open' });
        shadowErr.innerHTML = `<style>${WIDGET_STYLES}</style><div class="widget"><div class="offer-error"><h3>Configuração</h3><p>Não foi possível determinar a URL da API a partir do script. Contacte o suporte.</p></div></div>`;
        return;
    }

    const offerIdParam = options.offerId || '';
    const themeIdParam = options.themeId || '';
    const sellerId = options.sellerId || '';
    const campaignId = options.campaignId || '';
    const sessionId = resolveSessionId();

    const defaultTitle = 'GARANTA SUA VAGA';
    const defaultAcceptanceLabel = 'Li e aceito o termo de inscrição';
    let widgetTitle = defaultTitle;
    let defaultCountryId: number | null = null;
    let defaultFormThemeId: number | null = null;
    let acceptanceTermLabel = defaultAcceptanceLabel;
    let acceptanceTermPdfUrl: string | null = null;
    let showTermsModal = false;
    let termsScrolledToEnd = false;
    let termsPdfObserver: IntersectionObserver | null = null;
    let pagarmePublicKey: string | null = null;
    let paymentSimulation = false;
    let paymentSuccessMessage = '';
    let paymentErrorIntro = '';
    let institutionName: string | null = null;
    let contactUrl: string | null = null;
    let alreadyEnrolledHtml: string | null = null;
    let alreadyMatriculatedHtml: string | null = null;
    let paymentErrorContactHtml: string | null = null;
    let gtmContainerId: string | null = null;
    let datalayerEventsEnabled = false;
    let serverWidgetVersion: string | null = null;

    try {
        const bootstrap = await fetchBootstrap(apiBase);
        defaultCountryId = bootstrap.default_country_id;
        defaultFormThemeId = bootstrap.default_form_theme_id ?? null;
        const wt = bootstrap.widget_title;
        if (wt != null && String(wt).trim() !== '') {
            widgetTitle = String(wt).trim();
        }
        const al = bootstrap.acceptance_term_label;
        if (al != null && String(al).trim() !== '') {
            acceptanceTermLabel = String(al).trim();
        }
        acceptanceTermPdfUrl =
            (bootstrap as Record<string, unknown>).acceptance_term_pdf_url != null &&
            String((bootstrap as Record<string, unknown>).acceptance_term_pdf_url).trim() !== ''
                ? String((bootstrap as Record<string, unknown>).acceptance_term_pdf_url).trim()
                : null;
        const pk = bootstrap.pagarme_public_key;
        if (pk != null && String(pk).trim() !== '') {
            pagarmePublicKey = String(pk).trim();
        }
        paymentSimulation = bootstrap.payment_simulation === true;
        if (bootstrap.widget_payment_success_message != null && String(bootstrap.widget_payment_success_message).trim() !== '') {
            paymentSuccessMessage = String(bootstrap.widget_payment_success_message).trim();
        }
        if (bootstrap.widget_payment_error_intro != null && String(bootstrap.widget_payment_error_intro).trim() !== '') {
            paymentErrorIntro = String(bootstrap.widget_payment_error_intro).trim();
        }
        institutionName =
            (bootstrap as Record<string, unknown>).institution_name != null
                ? String((bootstrap as Record<string, unknown>).institution_name).trim() || null
                : null;
        contactUrl =
            (bootstrap as Record<string, unknown>).contact_url != null
                ? String((bootstrap as Record<string, unknown>).contact_url).trim() || null
                : null;
        alreadyEnrolledHtml =
            (bootstrap as Record<string, unknown>).already_enrolled_html != null
                ? String((bootstrap as Record<string, unknown>).already_enrolled_html).trim() || null
                : null;
        alreadyMatriculatedHtml =
            (bootstrap as Record<string, unknown>).already_matriculated_html != null
                ? String((bootstrap as Record<string, unknown>).already_matriculated_html).trim() || null
                : null;
        paymentErrorContactHtml =
            (bootstrap as Record<string, unknown>).payment_error_contact_html != null
                ? String((bootstrap as Record<string, unknown>).payment_error_contact_html).trim() || null
                : null;
        const gtmRaw = (bootstrap as Record<string, unknown>).gtm_container_id;
        gtmContainerId = gtmRaw != null && String(gtmRaw).trim() !== '' ? String(gtmRaw).trim().toUpperCase() : null;
        datalayerEventsEnabled = Boolean((bootstrap as Record<string, unknown>).datalayer_events_enabled);
        const sv = (bootstrap as Record<string, unknown>).widget_version;
        serverWidgetVersion = typeof sv === 'string' ? sv.trim() || null : null;
    } catch {
        /* mantém título e país padrão */
    }

    const shadow = root.attachShadow({ mode: 'open' });

    // Detecta widget.js desatualizado em cache do navegador: se o servidor
    // publicou uma versão diferente da que está rodando em memória, força
    // modal pedindo recarga. Em dev (__WIDGET_VERSION__='dev') ou quando o
    // servidor não retorna versão, não atua.
    if (serverWidgetVersion && __WIDGET_VERSION__ !== 'dev' && serverWidgetVersion !== __WIDGET_VERSION__) {
        renderUpdateRequiredModal(shadow);
        return;
    }

    if (gtmContainerId) {
        installGtm(gtmContainerId);
    }

    shadow.innerHTML = `
      <style>${WIDGET_STYLES}</style>
      <div class="widget"><h2>${escapeHtmlAttr(widgetTitle)}</h2>
      <div class="offer-loading"><span class="spinner"></span><p>Carregando oferta...</p></div></div>`;

    let offerPayload: ApiOfferResponse;
    let theme: WidgetTheme | null = null;

    try {
        const effectiveThemeId =
            themeIdParam && String(themeIdParam).trim() !== '' ? themeIdParam : defaultFormThemeId != null ? String(defaultFormThemeId) : '';
        const [offerJson, themeJson] = await Promise.all([fetchOfferLegacy(apiBase, offerIdParam), fetchFormTheme(apiBase, effectiveThemeId)]);
        offerPayload = offerJson;
        theme = themeJson
            ? {
                  backgroundColor: themeJson.backgroundColor ?? undefined,
                  textColor: themeJson.textColor ?? undefined,
                  accentColor: themeJson.accentColor ?? undefined,
                  inputBackgroundColor: themeJson.inputBackgroundColor ?? undefined,
                  fonts: themeJson.fonts ?? undefined,
                  code: themeJson.code ?? undefined,
              }
            : null;
    } catch (e) {
        console.error('[DecolaWidget]', e);
        shadow.innerHTML = `
        <style>${WIDGET_STYLES}</style>
        <div class="widget"><h2>${escapeHtmlAttr(widgetTitle)}</h2>
        <div class="offer-error"><h3>Erro ao carregar</h3><p>Não foi possível carregar a oferta. Tente novamente mais tarde.</p></div></div>`;
        return;
    }

    const offerNumericId = offerPayload.offer.id;
    const plansByCustomerAndMethod = buildPlansFromCombinations(offerPayload.combinations);
    const admissionTypes = offerPayload.admission_types;

    const initialTipo = (() => {
        const hasPlans = (at: (typeof admissionTypes)[number]): boolean => {
            const bucket = plansByCustomerAndMethod[`at_${at.id}`];
            if (!bucket) {
                return false;
            }

            return Object.values(bucket).some((plans) => Array.isArray(plans) && plans.length > 0);
        };

        // Padrão deve ser um tipo NÃO-egresso (ex.: Público Geral). "Egresso" só é
        // selecionado quando o webhook confirma (checkCpfInfo). Sem isto, se o
        // egresso vier primeiro na lista, uma falha/não-confirmação do webhook
        // deixaria o candidato como egresso indevidamente.
        const firstNonEgresso = admissionTypes.find((at) => hasPlans(at) && !/egresso/i.test(at.name));
        const firstWithPlans = firstNonEgresso ?? admissionTypes.find((at) => hasPlans(at));
        if (firstWithPlans) {
            return `at_${firstWithPlans.id}`;
        }

        return Object.keys(plansByCustomerAndMethod)[0] || 'at_0';
    })();

    let currentStep: number = STEPS.DADOS_BASICOS;
    let statesList: { id: number; name: string }[] = [];
    let cidadesList: { id: number; name: string }[] = [];
    let cepLoading = false;
    let termosError = false;
    let pagamentoError = false;
    let dadosPessoaisErrors: Record<string, string> = {};
    let enderecoErrors: Record<string, string> = {};
    let enrollmentSuccessId: number | null = null;
    let checkoutErrorMessage: string | null = null;
    let cardTokenizeErrorMessage: string | null = null;
    let submitting = false;
    /** Feedback rápido enquanto o botão "Continuar" dispara validações/HTTP entre steps. */
    let advancing = false;
    let couponError: string | null = null;
    let couponApplying = false;
    let lastEnrollmentId: number | null = null;
    /** Tela cheia: aguardando PIX/boleto, confirmação animada do PIX ou erro de cobrança. */
    let paymentUi: 'none' | 'pix' | 'boleto' | 'pix_received' | 'payment_error' = 'none';
    let pixPaymentData: { qr_code?: string | null; qr_code_url?: string | null } | null = null;
    let boletoPaymentData: { line?: string | null; pdf_url?: string | null; due_at?: string | null } | null = null;
    let paymentFailureMessage: string | null = null;
    /** Último meio usado em tentativa PIX/boleto (para re-tentar). */
    let lastPixBoletoMethod: 'pix' | 'boleto' | null = null;
    let pixPollTimer: ReturnType<typeof setInterval> | null = null;
    /** Momento em que o QR/código PIX foi exibido (para tempo mínimo antes da confirmação). */
    let pixAwaitStartedAt: number | null = null;
    let pixDeferredSuccessTimer: ReturnType<typeof setTimeout> | null = null;
    let pixAnimationEndTimer: ReturnType<typeof setTimeout> | null = null;
    let pixCountdownTimer: ReturnType<typeof setInterval> | null = null;
    let pixCountdownSeconds = 300;
    let successVariant: 'payment_confirmed' | 'boleto_generated' | 'pix_pending' = 'payment_confirmed';

    function clearPixConfirmationTimers(): void {
        if (pixDeferredSuccessTimer !== null) {
            clearTimeout(pixDeferredSuccessTimer);
            pixDeferredSuccessTimer = null;
        }
        if (pixAnimationEndTimer !== null) {
            clearTimeout(pixAnimationEndTimer);
            pixAnimationEndTimer = null;
        }
    }

    function stopPaymentPoll(): void {
        if (pixPollTimer) {
            clearInterval(pixPollTimer);
            pixPollTimer = null;
        }
        if (pixCountdownTimer) {
            clearInterval(pixCountdownTimer);
            pixCountdownTimer = null;
        }
        clearPixConfirmationTimers();
    }

    function schedulePixPaymentReveal(enrollmentId: number): void {
        clearPixConfirmationTimers();
        const started = pixAwaitStartedAt ?? Date.now();
        const elapsed = Date.now() - started;
        const waitMs = Math.max(0, PIX_MIN_MS_BEFORE_CONFIRM_UI - elapsed);

        pixDeferredSuccessTimer = window.setTimeout(() => {
            pixDeferredSuccessTimer = null;
            paymentUi = 'pix_received';
            void render();
            pixAnimationEndTimer = window.setTimeout(() => {
                pixAnimationEndTimer = null;
                successVariant = 'payment_confirmed';
                enrollmentSuccessId = enrollmentId;
                paymentUi = 'none';
                pixPaymentData = null;
                boletoPaymentData = null;
                pixAwaitStartedAt = null;
                void render();
            }, PIX_RECEIVED_ANIMATION_MS);
        }, waitMs);
    }

    const formData: FormDataState = {
        offerId: String(offerNumericId),
        themeId: themeIdParam,
        sellerId,
        campaignId,
        tipoIngresso: initialTipo,
        formaPagamento: 'pix',
        parcelas: 1,
        cupom: false,
        codigoCupom: '',
        coupon_id: null as number | null,
        coupon_discount_type: null as string | null,
        coupon_amount: null as number | null,
        coupon_code_display: '',
        coupon_scholarship_name: '',
        aceiteTermo: false,
        possuiNomeSocial: false,
        estrangeiro: false,
        cpfAlumniInfo: null as { is_alumni: boolean; alumni_admission_type_id: number | null } | null,
        cpfIsEnrolled: false as boolean,
        cpfHasMatricula: false as boolean,
        cpfCheckLoading: false as boolean,
        showAlreadyEnrolledScreen: false as boolean,
        blockReason: null as 'enrolled' | 'matricula' | null,
        couponAccordionOpen: false as boolean,
        themeCode: theme?.code ?? null,
        theme: theme?.code ?? null,
        payment_plan_id: null,
        admission_type_id: admissionTypeIdFromKey(initialTipo),
        country_id: defaultCountryId,
        state_id: null,
        city_id: null,
        geo_ip: null,
        latitude: null,
        longitude: null,
        geo_continent: null,
        geo_country: null,
        geo_region: null,
        geo_city: null,
    };

    function ensureFormaPagamentoForTipoIngresso(): void {
        const ct = String(formData.tipoIngresso);
        const methods = plansByCustomerAndMethod[ct] ?? {};
        const isForeign = Boolean(formData.estrangeiro);
        const order = ['pix', 'cartao', 'boleto'] as const;
        const available = order.filter((m) => {
            if ((methods[m]?.length ?? 0) === 0) return false;
            if (isForeign && (m === 'pix' || m === 'boleto')) return false;
            return true;
        });
        if (available.length === 0) {
            return;
        }
        const cur = String(formData.formaPagamento ?? '');
        if (!cur || (methods[cur]?.length ?? 0) === 0 || (isForeign && (cur === 'pix' || cur === 'boleto'))) {
            formData.formaPagamento = available[0];
        }
    }

    function getPlanOptionsForCurrentMethod(): PaymentPlanRow[] {
        const ct = String(formData.tipoIngresso);
        const pm = String(formData.formaPagamento);
        const list = (plansByCustomerAndMethod[ct] || {})[pm];

        return Array.isArray(list) ? list : [];
    }

    function ensurePaymentPlanForCurrentMethod(): void {
        const opts = getPlanOptionsForCurrentMethod();
        if (opts.length === 0) {
            return;
        }
        const curId = Number(formData.payment_plan_id);
        const ok = opts.some((p) => parseInt(String(p.id), 10) === curId);
        if (!ok) {
            formData.payment_plan_id = parseInt(String(opts[0].id), 10);
        }
    }

    ensureFormaPagamentoForTipoIngresso();
    ensurePaymentPlanForCurrentMethod();

    void fetchGeoJsGeo().then((geo) => {
        if (!geo) return;
        Object.assign(formData, geo);
    });

    void postWidgetEvent(apiBase, {
        event: 'widget_init',
        session_id: sessionId,
        offer_id: offerIdParam,
        theme_id: themeIdParam,
        payload: { sellerId, campaignId },
    });

    function getCurrentPlan(): PaymentPlanRow | null {
        const opts = getPlanOptionsForCurrentMethod();
        if (opts.length === 0) {
            return null;
        }
        const want = Number(formData.payment_plan_id);
        const found = opts.find((p) => parseInt(String(p.id), 10) === want);

        return found ?? opts[0] ?? null;
    }

    function syncPlanFromFormData(): void {
        const plan = getCurrentPlan();
        formData.payment_plan_id = plan ? parseInt(String(plan.id), 10) : null;
        formData.admission_type_id = admissionTypeIdFromKey(String(formData.tipoIngresso));
        const maxP = plan ? parseInt(String(plan.installments), 10) || 1 : 1;
        /** Parcelas do plano são fixas (N vezes), não "até N vezes". */
        formData.parcelas = maxP;
    }

    syncPlanFromFormData();

    function collectStepData(): void {
        shadow.querySelectorAll('input').forEach((input) => {
            const el = input as HTMLInputElement;
            if (el.type === 'checkbox') {
                if (el.id) formData[el.id] = el.checked;
            } else if (el.id) formData[el.id] = el.value || '';
        });
        shadow.querySelectorAll('select').forEach((sel) => {
            const el = sel as HTMLSelectElement;
            if (el.id === 'state_id' || el.id === 'city_id') {
                const n = parseInt(el.value, 10);
                formData[el.id] = Number.isNaN(n) ? null : n;
            } else if (el.id === 'pagamento_plano_id') {
                const n = parseInt(el.value, 10);
                formData.payment_plan_id = Number.isNaN(n) ? null : n;
            } else if (el.id) formData[el.id] = el.value || '';
        });
    }

    function buildEnrollmentBody(): Record<string, unknown> {
        collectStepData();
        const body: Record<string, unknown> = {
            offer_id: offerNumericId,
            payment_plan_id: formData.payment_plan_id,
            admission_type_id: formData.admission_type_id,
            coupon_id: formData.coupon_id != null && Number(formData.coupon_id) > 0 ? Number(formData.coupon_id) : null,
            widget_installments: Math.max(1, parseInt(String(formData.parcelas), 10) || 1),
            name: formData.nome,
            social_name: formData.possuiNomeSocial ? formData.nomeSocial : null,
            cpf: formData.estrangeiro ? null : formData.cpf,
            passport: formData.estrangeiro ? formData.passaporte : null,
            email: formData.email,
            phone: formData.telefone,
            postal_code: formData.cep,
            country_id: formData.country_id,
            state_id: formData.state_id,
            city_id: formData.city_id,
            neighborhood: formData.bairro,
            street: formData.endereco,
            street_number: formData.numero,
            complement: formData.complemento || null,
            session_id: sessionId,
        };
        appendWidgetGeoToPayload(body, formData);
        return body;
    }

    async function registerEnrollmentOnly(): Promise<number> {
        collectStepData();
        syncPlanFromFormData();
        const body = buildEnrollmentBody();
        const res = await postEnrollment(apiBase, body);
        enqueueWidgetEnrollmentIntegration(apiBase, res.id, sessionId);
        void postWidgetEvent(apiBase, {
            event: 'enrollment_created',
            session_id: sessionId,
            offer_id: offerIdParam,
            theme_id: themeIdParam,
            payload: { enrollment_id: res.id },
        });
        return res.id;
    }

    function startPaymentPolling(enrollmentId: number): void {
        stopPaymentPoll();
        pixPollTimer = setInterval(() => {
            void (async () => {
                try {
                    const st = await getWidgetPaymentStatus(apiBase, enrollmentId, sessionId);
                    if (st.paid) {
                        stopPaymentPoll();
                        if (lastPixBoletoMethod === 'pix') {
                            successVariant = 'payment_confirmed';
                            schedulePixPaymentReveal(enrollmentId);
                        } else {
                            successVariant = 'payment_confirmed';
                            enrollmentSuccessId = enrollmentId;
                            paymentUi = 'none';
                            pixPaymentData = null;
                            boletoPaymentData = null;
                            pixAwaitStartedAt = null;
                            await render();
                        }
                    }
                } catch {
                    /* polling silencioso */
                }
            })();
        }, 4000);
    }

    /**
     * Checkout simulado (homologação sem Pagar.me). Só monta a tela de cobrança
     * com dados fake — o pagamento é registrado apenas em "Concluir"
     * ({@see confirmSimulatedPayment}). Confirmar aqui marcava pago assim que a
     * tela abria, e o ambiente perdia a janela de voltar e trocar a forma de
     * pagamento (o cenário que mais precisa ser testado em homologação).
     */
    async function startSimulatedPayment(): Promise<void> {
        submitting = false;
        boletoPaymentData = null;
        paymentFailureMessage = null;
        pixPaymentData = { qr_code: SIMULATED_PIX_CODE, qr_code_url: null };
        paymentUi = 'pix';
        pixAwaitStartedAt = Date.now();

        await render();
    }

    /**
     * Registra o pagamento simulado no backend (status pago + baixa no RM) ao
     * concluir. Falha mantém o aluno na tela de erro, como no fluxo real.
     */
    async function confirmSimulatedPayment(enrollmentId: number): Promise<void> {
        try {
            await simulateWidgetPayment(apiBase, enrollmentId, sessionId);
        } catch (e) {
            paymentFailureMessage = e instanceof Error ? e.message : 'Não foi possível concluir o pagamento simulado.';
            paymentUi = 'payment_error';
            await render();
            return;
        }

        successVariant = 'payment_confirmed';
        enrollmentSuccessId = enrollmentId;
        paymentUi = 'none';
        pixPaymentData = null;
        boletoPaymentData = null;
        pixAwaitStartedAt = null;
        await render();
    }

    async function startPaymentForEnrollment(enrollmentId: number, method: 'pix' | 'boleto'): Promise<void> {
        submitting = false;
        paymentUi = 'none';
        pixPaymentData = null;
        boletoPaymentData = null;
        paymentFailureMessage = null;
        lastPixBoletoMethod = method;
        pixAwaitStartedAt = null;
        if (method === 'pix') {
            pixCountdownSeconds = 300;
            if (pixCountdownTimer) clearInterval(pixCountdownTimer);
            pixCountdownTimer = setInterval(() => {
                pixCountdownSeconds = Math.max(0, pixCountdownSeconds - 1);
                const el = shadow.getElementById('pix-countdown');
                if (el) {
                    const m = Math.floor(pixCountdownSeconds / 60)
                        .toString()
                        .padStart(2, '0');
                    const s = (pixCountdownSeconds % 60).toString().padStart(2, '0');
                    el.textContent = `${m}:${s}`;
                }
            }, 1000);
        }
        try {
            const data = await postWidgetPayment(apiBase, enrollmentId, {
                session_id: sessionId,
                payment_method: method,
            });
            if (method === 'pix') {
                pixPaymentData = data.pix ?? {};
                paymentUi = 'pix';
                pixAwaitStartedAt = Date.now();
            } else {
                boletoPaymentData = data.boleto ?? {};
                paymentUi = 'boleto';
            }
            startPaymentPolling(enrollmentId);
        } catch (e) {
            paymentFailureMessage = e instanceof Error ? e.message : 'Não foi possível iniciar o pagamento.';
            paymentUi = 'payment_error';
        }
        await render();
    }

    async function syncProgress(stage: WidgetEnrollmentProgressStage): Promise<boolean> {
        try {
            collectStepData();
            syncPlanFromFormData();
            const payload: Record<string, unknown> = {
                session_id: sessionId,
                offer_id: offerNumericId,
                stage,
            };
            if (stage === 'dados_basicos') {
                payload.name = String(formData.nome || '').trim();
                payload.email = String(formData.email || '').trim();
                payload.social_name = formData.possuiNomeSocial ? String(formData.nomeSocial || '').trim() || null : null;
                payload.cpf = formData.estrangeiro ? null : String(formData.cpf || '').trim() || null;
                payload.passport = formData.estrangeiro ? String(formData.passaporte || '').trim() || null : null;
                payload.phone = String(formData.telefone || '').trim() || null;
            }
            if (stage === 'dados_endereco') {
                payload.postal_code = String(formData.cep || '').replace(/\D/g, '') || null;
                payload.country_id = formData.country_id ?? null;
                payload.state_id = formData.state_id ?? null;
                payload.city_id = formData.city_id ?? null;
                payload.neighborhood = String(formData.bairro || '').trim() || null;
                payload.street = String(formData.endereco || '').trim() || null;
                payload.street_number = String(formData.numero || '').trim() || null;
                payload.complement = String(formData.complemento || '').trim() || null;
            }
            if (stage === 'pagamento') {
                payload.payment_plan_id = formData.payment_plan_id;
                payload.admission_type_id = formData.admission_type_id;
                if (formData.coupon_id != null && Number(formData.coupon_id) > 0) {
                    payload.coupon_id = Number(formData.coupon_id);
                }
            }
            appendWidgetGeoToPayload(payload, formData);
            await postEnrollmentProgress(apiBase, payload as Parameters<typeof postEnrollmentProgress>[1]);
            return true;
        } catch (e) {
            checkoutErrorMessage = e instanceof Error ? e.message : 'Erro ao sincronizar inscrição.';
            void render();
            return false;
        }
    }

    async function ensureStates(): Promise<void> {
        if (statesList.length > 0 || !defaultCountryId) return;
        try {
            statesList = await fetchStates(apiBase, defaultCountryId);
        } catch {
            statesList = [];
        }
    }

    async function loadCitiesForState(stateId: number): Promise<void> {
        if (!stateId) {
            cidadesList = [];
            return;
        }
        try {
            cidadesList = await fetchCities(apiBase, stateId);
        } catch {
            cidadesList = [];
        }
    }

    function renderProgressBar(step: number): string {
        const bars = Array.from({ length: STEP_TOTAL }, (_, i) => {
            const stepNum = i + 1;
            const isActive = stepNum === step;
            const isDone = stepNum < step;
            const cls = isDone ? 'progress-bar-done' : isActive ? 'progress-bar-active' : 'progress-bar-pending';
            return `<div class="progress-bar-segment ${cls}"></div>`;
        }).join('');
        return `<div class="widget-progress-bar" role="progressbar" aria-valuenow="${step}" aria-valuemax="${STEP_TOTAL}">${bars}</div>`;
    }

    async function applyCouponFlow(): Promise<void> {
        collectStepData();
        couponError = null;
        const code = String(formData.codigoCupom || '').trim();
        if (!code) {
            couponError = 'Informe o código do cupom.';
            await render();
            return;
        }
        couponApplying = true;
        await render();
        try {
            const res = await validateWidgetCoupon(apiBase, { offer_id: offerNumericId, code });
            formData.coupon_id = res.coupon_id;
            formData.coupon_discount_type = String(res.discount_type);
            formData.coupon_amount = parsePrice(res.amount);
            formData.coupon_code_display = res.code;
            formData.coupon_scholarship_name = res.scholarship_name || '';
            couponError = null;
        } catch (e) {
            formData.coupon_id = null;
            formData.coupon_discount_type = null;
            formData.coupon_amount = null;
            formData.coupon_code_display = '';
            formData.coupon_scholarship_name = '';
            couponError = e instanceof Error ? e.message : 'Cupom inválido.';
        } finally {
            couponApplying = false;
            await render();
        }
    }

    function renderAlreadyEnrolledScreen(): string {
        const isMatricula = formData.blockReason === 'matricula';
        const customHtml = isMatricula ? alreadyMatriculatedHtml : alreadyEnrolledHtml;
        if (customHtml) {
            return `
      <div class="already-enrolled-screen already-enrolled-screen-custom">
        <div class="already-enrolled-icon">✓</div>
        <div class="already-enrolled-custom-body">${customHtml}</div>
      </div>`;
        }
        const inst = institutionName ? `À ${escapeHtmlAttr(institutionName)}!` : '!';
        const title = isMatricula ? 'Você já tem matrícula efetivada nesse curso!' : 'Você já está matriculado nesse curso!';
        const body = isMatricula
            ? 'Identificamos que você já tem matrícula efetivada nesse curso.'
            : 'Identificamos que você já está matriculado nesse curso.';
        return `
      <div class="already-enrolled-screen">
        <div class="already-enrolled-icon">✓</div>
        <p class="already-enrolled-title">${title}</p>
        <p class="already-enrolled-body">${body}</p>
        <p class="already-enrolled-body">Caso você esteja com dúvidas sobre o pagamento ou início das aulas, entre em contato conosco${inst !== '!' ? ` ${inst}` : '.'}</p>
      </div>`;
    }

    function renderDadosBasicosStep(): string {
        if (formData.showAlreadyEnrolledScreen) {
            return renderAlreadyEnrolledScreen();
        }

        const est = Boolean(formData.estrangeiro);
        const alumniInfo = formData.cpfAlumniInfo as { is_alumni: boolean; alumni_admission_type_id: number | null } | null;
        const isAlumni = alumniInfo?.is_alumni ?? false;
        const isEnrolled = Boolean(formData.cpfIsEnrolled);
        const hasMatricula = Boolean(formData.cpfHasMatricula);
        const cpfLoading = Boolean(formData.cpfCheckLoading);

        const cpfBadgeHtml = isAlumni ? `<span class="cpf-alumni-badge">Desconto ex-aluno ✓</span>` : '';

        const cpfAlumniMsgHtml = isAlumni ? `<div class="cpf-alumni-message">Desconto válido para diplomado aplicado!</div>` : '';

        const enrolledMsgHtml =
            isEnrolled || hasMatricula
                ? `<div class="cpf-enrolled-warning">⚠ ${hasMatricula ? 'Você já tem matrícula efetivada nesse curso!' : 'Você já está matriculado nesse curso!'}</div>`
                : '';

        return `
      <div class="field">
        <label>Nome</label>
        <input type="text" id="nome" placeholder="${formData.possuiNomeSocial ? 'Conforme seu documento de identidade' : 'Digite seu nome'}" value="${escapeHtmlAttr(String(formData.nome || ''))}" maxlength="100">
        ${dadosPessoaisErrors.nome ? `<div class="field-error">${escapeHtmlAttr(dadosPessoaisErrors.nome)}</div>` : ''}
        ${
            formData.possuiNomeSocial
                ? ''
                : `<div class="add-nome-social-row"><button type="button" class="btn-add-nome-social" id="btn-add-nome-social">+ Adicionar Nome Social</button></div>`
        }
      </div>
      ${
          formData.possuiNomeSocial
              ? `<div class="field nome-social-field">
        <div class="nome-social-row">
          <label>Nome social<span class="hint-icon" title="Nome social se refere à designação pela qual a pessoa travesti ou transexual se identifica e é socialmente reconhecida.">i</span></label>
          <button type="button" class="btn-remove-nome-social" id="btn-remove-nome-social" title="Remover nome social" aria-label="Remover nome social">×</button>
        </div>
        <input type="text" id="nomeSocial" placeholder="Conforme você prefere ser chamado" value="${escapeHtmlAttr(String(formData.nomeSocial || ''))}" maxlength="100">
      </div>`
              : ''
      }
      <div class="field">
        <label>E-mail</label>
        <input type="email" id="email" placeholder="Digite seu e-mail" value="${escapeHtmlAttr(String(formData.email || ''))}" maxlength="250">
        ${dadosPessoaisErrors.email ? `<div class="field-error">${escapeHtmlAttr(dadosPessoaisErrors.email)}</div>` : ''}
      </div>
      <div class="field">
        <label>Celular/Whatsapp</label>
        <input type="tel" id="telefone" placeholder="Digite seu número de celular" value="${escapeHtmlAttr(String(formData.telefone || ''))}" maxlength="16">
        ${dadosPessoaisErrors.telefone ? `<div class="field-error">${escapeHtmlAttr(dadosPessoaisErrors.telefone)}</div>` : ''}
      </div>
      <div class="field">
        <div class="field-label-row">
          <label>${est ? 'Passaporte' : 'CPF'}</label>
          <label class="check-inline"><input type="checkbox" id="estrangeiro" ${est ? 'checked' : ''}> Sou estrangeiro</label>
        </div>
        <div class="cpf-input-wrap">
          <input type="text" id="${est ? 'passaporte' : 'cpf'}"
            placeholder="${est ? 'Digite seu número de passaporte' : 'Digite seu número de CPF'}"
            value="${escapeHtmlAttr(est ? String(formData.passaporte || '') : String(formData.cpf || ''))}"
            ${!est ? 'maxlength="14"' : ''}
            ${cpfLoading ? 'disabled' : ''}>
          ${cpfBadgeHtml}
        </div>
        ${dadosPessoaisErrors.cpf ? `<div class="field-error">${escapeHtmlAttr(dadosPessoaisErrors.cpf)}</div>` : ''}
        ${dadosPessoaisErrors.passaporte ? `<div class="field-error">${escapeHtmlAttr(dadosPessoaisErrors.passaporte)}</div>` : ''}
        ${cpfAlumniMsgHtml}
        ${enrolledMsgHtml}
      </div>`;
    }

    /**
     * Último CPF (só dígitos) cujo cpf-info já foi consultado com sucesso. Evita
     * re-consultar a API quando o blur do CPF é seguido imediatamente de um clique
     * no btn-next — sem essa idempotência, o re-render disparado pelo blur destrói
     * o botão antes do click chegar, causando o famoso "preciso clicar 2x".
     */
    let lastCheckedKey: string | null = null;

    /** Chave de idempotência da verificação: e-mail + CPF (estrangeiro usa só e-mail). */
    function candidateCheckKey(email: string, cpfDigits: string, estrangeiro: boolean): string | null {
        const e = email.trim().toLowerCase();
        if (e === '') {
            return null;
        }
        if (estrangeiro) {
            return `est|${e}`;
        }
        return cpfDigits.length === 11 ? `cpf|${e}|${cpfDigits}` : null;
    }

    async function checkCpfInfo(email: string, cpf: string): Promise<void> {
        const estrangeiro = Boolean(formData.estrangeiro);
        const digits = cpf.replace(/\D/g, '');
        const key = candidateCheckKey(email, digits, estrangeiro);
        if (key === null) {
            formData.cpfAlumniInfo = null;
            formData.cpfIsEnrolled = false;
            formData.cpfHasMatricula = false;
            lastCheckedKey = null;
            return;
        }
        if (key === lastCheckedKey && !formData.cpfCheckLoading) {
            return;
        }
        formData.cpfCheckLoading = true;
        void render();
        try {
            const info = await fetchCpfInfo(apiBase, email, estrangeiro ? '' : cpf, offerNumericId);
            lastCheckedKey = key;
            formData.cpfAlumniInfo = { is_alumni: info.is_alumni, alumni_admission_type_id: info.alumni_admission_type_id };
            formData.cpfIsEnrolled = info.is_enrolled;
            formData.cpfHasMatricula = info.has_matricula;
            if (info.is_alumni && info.alumni_admission_type_id) {
                formData.tipoIngresso = `at_${info.alumni_admission_type_id}`;
                formData.admission_type_id = info.alumni_admission_type_id;
                ensureFormaPagamentoForTipoIngresso();
                ensurePaymentPlanForCurrentMethod();
                syncPlanFromFormData();
            }
        } catch {
            formData.cpfAlumniInfo = null;
            formData.cpfIsEnrolled = false;
            formData.cpfHasMatricula = false;
            lastCheckedKey = null;
        } finally {
            formData.cpfCheckLoading = false;
            void render();
        }
    }

    function buildPaymentDiscountLabel(method: string, plan: PaymentPlanRow | null): string {
        if (!plan) return method === 'pix' ? 'PIX' : method === 'boleto' ? 'Boleto' : 'Cartão de crédito';
        const disc = parsePrice(plan.discount ?? 0);
        const orig = parsePrice(plan.price ?? 0);
        if (disc <= 0 || orig <= 0) {
            return method === 'pix' ? 'PIX' : method === 'boleto' ? 'Boleto' : 'Cartão de crédito';
        }
        const pct = Math.round((disc / orig) * 100);
        if (method === 'pix') return `PIX (${pct}% OFF)`;
        if (method === 'boleto') return `Boleto (${pct}% OFF)`;
        return 'Cartão de crédito';
    }

    function buildInstallmentsSubtitle(plans: PaymentPlanRow[]): string {
        const installments = plans.map((p) => parseInt(String(p.installments), 10) || 1);
        const hasUpfront = installments.includes(1);
        const max = Math.max(...installments);

        if (hasUpfront && max > 1) return `À vista ou em até ${max}x`;
        if (hasUpfront) return 'À vista';
        if (max > 1) return `Em até ${max}x`;
        return `Em ${max}x`;
    }

    function buildInvestmentTags(plan: PaymentPlanRow | null, couponDisc: number): string {
        const items: Array<{ type: 'payment' | 'alumni' | 'coupon'; text: string }> = [];
        const fp = String(formData.formaPagamento || '');
        const discVal = plan ? parsePrice(plan.discount ?? 0) : 0;
        const orig = plan ? parsePrice(plan.price ?? 0) : 0;
        const installments = plan ? parseInt(String(plan.installments), 10) || 1 : 1;
        const methodName = fp === 'pix' ? 'Pix' : fp === 'boleto' ? 'Boleto' : 'Cartão';
        const basePaymentLabel = installments > 1 ? `Em ${installments}x no ${methodName}` : `À vista no ${methodName}`;

        if (discVal > 0 && orig > 0 && fp !== 'cartao') {
            const pct = Math.round((discVal / orig) * 100);
            items.push({ type: 'payment', text: `${basePaymentLabel} (${pct}% de desconto)` });
        } else {
            items.push({ type: 'payment', text: basePaymentLabel });
        }
        const alumniInfo = formData.cpfAlumniInfo as { is_alumni: boolean } | null;
        if (alumniInfo?.is_alumni) {
            items.push({ type: 'alumni', text: 'Desconto ex-aluno' });
        }
        if (couponDisc > 0) {
            items.push({ type: 'coupon', text: 'Cupom aplicado' });
        }

        const paymentIcon =
            '<svg class="investment-tag-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/></svg>';
        const alumniIcon =
            '<svg class="investment-tag-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>';
        const couponIcon =
            '<svg class="investment-tag-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z"/><line x1="13" y1="6" x2="13" y2="18" stroke-dasharray="2 2"/></svg>';

        return items
            .map((it) => {
                const icon = it.type === 'payment' ? paymentIcon : it.type === 'alumni' ? alumniIcon : couponIcon;
                return `<div class="investment-tag">${icon}<span>${escapeHtmlAttr(it.text)}</span></div>`;
            })
            .join('');
    }

    function renderPlanoStep(): string {
        ensureFormaPagamentoForTipoIngresso();
        ensurePaymentPlanForCurrentMethod();
        const ct = String(formData.tipoIngresso);
        const methods = plansByCustomerAndMethod[ct] || {};
        const plan = getCurrentPlan();
        const planNet = plan ? getPlanNetPrice(plan) : 0;
        const originalPrice = plan ? parsePrice(plan.price ?? 0) : 0;
        const couponDisc =
            plan && formData.coupon_discount_type != null && formData.coupon_amount != null
                ? computeScholarshipDiscount(planNet, String(formData.coupon_discount_type), Number(formData.coupon_amount))
                : 0;
        const finalTotal = Math.max(0, planNet - couponDisc);
        const fp = String(formData.formaPagamento || 'pix');
        const couponOpen = Boolean(formData.couponAccordionOpen);
        const couponApplied = formData.coupon_id != null && Number(formData.coupon_id) > 0;

        const isForeign = Boolean(formData.estrangeiro);
        const methodOrder = ['pix', 'boleto', 'cartao'] as const;
        const availableMethods = methodOrder.filter((m) => {
            if ((methods[m]?.length ?? 0) === 0) return false;
            if (isForeign && (m === 'pix' || m === 'boleto')) return false;
            return true;
        });

        const methodsHtml = availableMethods
            .map((m) => {
                const plans = methods[m] as PaymentPlanRow[];
                const firstPlan = plans[0];
                const isSelected = fp === m;
                const label = buildPaymentDiscountLabel(m, firstPlan);
                const subtitle = buildInstallmentsSubtitle(plans);
                // Mostra dropdown sempre que houver mais de um plano cadastrado para
                // o método selecionado (cartão, boleto ou PIX). Sem gerar opções
                // dinâmicas — exibe apenas o que foi cadastrado em payment_plans.
                const installmentOpts = isSelected ? getPlanOptionsForCurrentMethod() : [];
                const hasExpansion = installmentOpts.length > 1;
                let expansionHtml = '';
                if (hasExpansion) {
                    const radioOpts = installmentOpts
                        .map((pl) => {
                            const n = parseInt(String(pl.installments), 10) || 1;
                            const plNet = getPlanNetPrice(pl);
                            const plCouponDisc =
                                formData.coupon_discount_type != null && formData.coupon_amount != null
                                    ? computeScholarshipDiscount(plNet, String(formData.coupon_discount_type), Number(formData.coupon_amount))
                                    : 0;
                            const plFinal = Math.max(0, plNet - plCouponDisc);
                            const pid = parseInt(String(pl.id), 10);
                            const sel = Number(formData.payment_plan_id) === pid;
                            const lbl =
                                n > 1
                                    ? `${n}x — ${formatPrice(calcParcelamento(plFinal, n).valorExibicao)}`
                                    : `À vista — ${formatPrice(plFinal)}`;
                            return `<label class="installment-radio ${sel ? 'selected' : ''}" data-plan-id="${pid}">
                      <input type="radio" name="installment" value="${pid}" ${sel ? 'checked' : ''} data-plan-id="${pid}">
                      <span class="radio-dot"></span>
                      ${escapeHtmlAttr(lbl)}
                    </label>`;
                        })
                        .join('');
                    expansionHtml = `<div class="installment-options">${radioOpts}</div>`;
                }
                return `<div class="payment-method-card ${isSelected ? 'selected' : ''}" data-pag="${m}">
          <div class="payment-method-header">
            <span class="payment-method-radio ${isSelected ? 'checked' : ''}"></span>
            <div class="payment-method-text">
              <span class="payment-method-label">${escapeHtmlAttr(label)}</span>
              <span class="payment-method-subtitle">${escapeHtmlAttr(subtitle)}</span>
            </div>
            ${hasExpansion ? '<span class="payment-method-chevron">▴</span>' : ''}
          </div>
          ${expansionHtml}
        </div>`;
            })
            .join('');

        const courseNameHtml = offerPayload?.offer?.name
            ? `<div class="investment-course-header">${escapeHtmlAttr(String(offerPayload.offer.name))}</div>`
            : '';

        const hasDiscount = (plan && parsePrice(plan.discount ?? 0) > 0) || couponDisc > 0;
        const investmentHtml = `
      <div class="investment-summary${courseNameHtml ? ' has-course-header' : ''}">
        ${courseNameHtml}
        <div class="investment-summary-body">
          <div class="investment-label">Resumo do investimento</div>
          ${hasDiscount ? `<div class="investment-original">De ${formatPrice(originalPrice)} por</div>` : ''}
          <div class="investment-price">${formatPrice(finalTotal)}</div>
          ${buildInvestmentTags(plan, couponDisc)}
        </div>
      </div>`;

        const couponBadgeHtml = couponApplied
            ? `<span class="coupon-badge-valid">Cupom aplicado ✓</span>`
            : couponError
              ? `<span class="coupon-badge-invalid">Cupom inválido ✗</span>`
              : '';
        const couponInvalid = couponError !== null && !couponApplied;
        const couponInputHtml = couponOpen
            ? `<div class="coupon-input-row">
            <input type="text" id="codigoCupom" class="${couponInvalid ? 'coupon-input-invalid' : ''}" placeholder="Cupom" value="${escapeHtmlAttr(String(formData.codigoCupom || ''))}" maxlength="64" autocomplete="off">
            ${couponBadgeHtml}
           </div>`
            : '';

        const couponHtml = `
      <div class="coupon-accordion">
        <div class="coupon-accordion-header" id="btn-coupon-accordion">
          <span>Possui cupom de desconto?</span>
          <span class="coupon-chevron">${couponOpen ? '▴' : '▾'}</span>
        </div>
        ${couponInputHtml}
      </div>`;

        const pagamentoErrorHtml = pagamentoError
            ? '<div class="field-error" style="margin-top:0.5rem">Nenhum plano de pagamento disponível.</div>'
            : '';

        // Bloco do termo de aceite (mensagem + checkbox read-only). Clicar abre o
        // modal de visualização; o checkbox só é marcado após o "SIM" no modal.
        // Só aparece quando há modelo de termo (acceptanceTermPdfUrl).
        const termosHtml = acceptanceTermPdfUrl
            ? `<div class="termos">
        <label class="termos-aceite-label" id="termos-aceite-label">
          <input type="checkbox" id="aceiteTermo" ${formData.aceiteTermo ? 'checked' : ''} disabled aria-readonly="true">
          <span>${escapeHtmlAttr(acceptanceTermLabel)}</span>
        </label>
        ${termosError ? '<div class="termos-error">É obrigatório aceitar o termo para continuar.</div>' : ''}
      </div>`
            : '';

        return `
      ${methodsHtml}
      ${investmentHtml}
      ${couponHtml}
      ${pagamentoErrorHtml}
      ${termosHtml}`;
    }

    function renderPagamentoStep(): string {
        ensureFormaPagamentoForTipoIngresso();
        ensurePaymentPlanForCurrentMethod();
        const ct = String(formData.tipoIngresso);
        const methods = plansByCustomerAndMethod[ct] || {};
        const plan = getCurrentPlan();
        const planNet = plan ? getPlanNetPrice(plan) : 0;
        const originalPrice = plan ? parsePrice(plan.price ?? 0) : 0;
        const discountVal = plan ? parsePrice(plan.discount ?? 0) : 0;
        const couponDisc =
            plan && formData.coupon_discount_type != null && formData.coupon_amount != null
                ? computeScholarshipDiscount(planNet, String(formData.coupon_discount_type), Number(formData.coupon_amount))
                : 0;
        const finalTotal = Math.max(0, planNet - couponDisc);
        const price = plan ? formatPrice(planNet) : '—';
        let priceHtml = price;
        if (plan && couponDisc > 0) {
            priceHtml = `<span class="valor-original">De ${formatPrice(planNet)}</span> por ${formatPrice(finalTotal)}`;
        } else if (plan && discountVal > 0) {
            priceHtml = `<span class="valor-original">De ${formatPrice(originalPrice)}</span> por ${formatPrice(planNet)}`;
        }
        const descontoLinhas: string[] = [];
        if (plan && discountVal > 0) {
            descontoLinhas.push(`<div class="desconto">Desconto do plano: ${formatPrice(discountVal)}</div>`);
        }
        if (plan && couponDisc > 0) {
            const nomeCupom = String(formData.coupon_scholarship_name || formData.coupon_code_display || '').trim();
            descontoLinhas.push(`<div class="desconto">Cupom${nomeCupom ? ` (${escapeHtmlAttr(nomeCupom)})` : ''}: ${formatPrice(couponDisc)}</div>`);
        }
        const descontoHtml = descontoLinhas.join('');
        const maxParcelas = parseInt(String(plan?.installments), 10) || 1;
        const { valorExibicao } = plan && maxParcelas > 1 ? calcParcelamento(finalTotal, maxParcelas) : { valorExibicao: finalTotal };

        let resumoTexto = '';
        if (plan) {
            if (maxParcelas > 1) {
                resumoTexto = `em ${maxParcelas}x de ${formatPrice(valorExibicao)}`;
            } else if (formData.formaPagamento === 'pix') {
                resumoTexto = 'À vista no PIX';
            } else if (formData.formaPagamento === 'cartao') {
                resumoTexto = 'À vista no cartão';
            } else {
                resumoTexto = 'À vista no boleto';
            }
        }

        const paymentLabels: Record<string, string> = { pix: 'PIX', cartao: 'Cartão de crédito', boleto: 'Boleto' };
        const paymentOptions = ['pix', 'cartao', 'boleto']
            .filter((m) => (methods[m]?.length ?? 0) > 0)
            .map((m) => {
                const pList = methods[m] as PaymentPlanRow[];
                const first = pList[0];
                const installmentsNum = parseInt(String(first?.installments), 10) || 1;
                const small =
                    pList.length > 1 ? `${pList.length} opções de parcelamento` : installmentsNum > 1 ? `Em ${installmentsNum}x` : 'À vista';
                const description = String(first?.description || '').trim();
                return { key: m, label: paymentLabels[m] || m, small, description };
            });

        let tipoIngressoHtml = '';
        if (admissionTypes.length > 1) {
            tipoIngressoHtml = `
        <div class="section-label">Tipo de ingresso</div>
        <div class="btn-group">
          ${admissionTypes
              .map(
                  (at) => `
          <button type="button" class="btn-tipo ${ct === `at_${at.id}` ? 'selected' : ''}" data-admission="${at.id}">${escapeHtmlAttr(at.name)}</button>`,
              )
              .join('')}
        </div>`;
        }

        const planOptionsList = getPlanOptionsForCurrentMethod();
        let parcelasHtml = '';
        if (planOptionsList.length > 1 && plan) {
            const optsHtml = planOptionsList
                .map((pl) => {
                    const plNet = getPlanNetPrice(pl);
                    const plCouponDisc =
                        formData.coupon_discount_type != null && formData.coupon_amount != null
                            ? computeScholarshipDiscount(plNet, String(formData.coupon_discount_type), Number(formData.coupon_amount))
                            : 0;
                    const plFinal = Math.max(0, plNet - plCouponDisc);
                    const n = parseInt(String(pl.installments), 10) || 1;
                    const { valorExibicao: vParc } = n > 1 ? calcParcelamento(plFinal, n) : { valorExibicao: plFinal };
                    const nome = String(pl.name || '').trim();
                    const label =
                        n > 1
                            ? `${nome ? `${nome} — ` : ''}Em ${n}x de ${formatPrice(vParc)}`
                            : `${nome ? `${nome} — ` : ''}${formatPrice(plFinal)} à vista`;
                    const pid = parseInt(String(pl.id), 10);
                    const sel = Number(formData.payment_plan_id) === pid;

                    return `<option value="${pid}" ${sel ? 'selected' : ''}>${escapeHtmlAttr(label)}</option>`;
                })
                .join('');
            parcelasHtml = `
        <div class="field field-parcelamento field-parcelamento-select">
          <label>Parcelamento</label>
          <select id="pagamento_plano_id">${optsHtml}</select>
        </div>`;
        } else if (maxParcelas > 1 && plan) {
            const vp = calcParcelamento(finalTotal, maxParcelas).valorExibicao;
            parcelasHtml = `
        <div class="field field-parcelamento">
          <label>Parcelamento</label>
          <p class="parcelamento-valor">Em ${maxParcelas}x de ${formatPrice(vp)}</p>
        </div>`;
        }

        return `
      ${tipoIngressoHtml}
      <div class="section-label">Forma de pagamento</div>
      ${paymentOptions.length === 0 ? '<p class="field-error">Nenhum plano de pagamento disponível.</p>' : ''}
      ${paymentOptions
          .map(
              (opt) => `
        <div class="payment-option ${formData.formaPagamento === opt.key ? 'selected' : ''}" data-pag="${opt.key}">
          <input type="radio" name="pag" value="${opt.key}" ${formData.formaPagamento === opt.key ? 'checked' : ''}>
          <div class="text">${opt.label}${opt.description ? ` <span class="payment-desc">${escapeHtmlAttr(opt.description)}</span>` : ''}${opt.small ? `<small>${opt.small}</small>` : ''}</div>
        </div>`,
          )
          .join('')}
      ${parcelasHtml}
      <div class="field">
        <label class="check"><input type="checkbox" id="cupom" ${formData.cupom ? 'checked' : ''}> Possui cupom de desconto?</label>
      </div>
      ${
          formData.cupom
              ? `<div class="field">
        <label>Código do cupom</label>
        <div class="cupom-row">
          <input type="text" id="codigoCupom" name="codigoCupom" autocomplete="off" placeholder="Digite o código" value="${escapeHtmlAttr(String(formData.codigoCupom || ''))}" maxlength="64">
          <button type="button" class="btn-cupom-aplicar" id="btn-aplicar-cupom" ${couponApplying ? 'disabled' : ''}>${couponApplying ? '…' : 'Aplicar'}</button>
        </div>
        ${couponError ? `<div class="cupom-error">${escapeHtmlAttr(couponError)}</div>` : ''}
      </div>`
              : ''
      }
      <div class="resumo">
        <div class="valor">${priceHtml}</div>
        ${descontoHtml}
        <div class="obs">${resumoTexto}</div>
      </div>
      ${
          acceptanceTermPdfUrl
              ? `<div class="termos">
        <label class="termos-aceite-label" id="termos-aceite-label">
          <input type="checkbox" id="aceiteTermo" ${formData.aceiteTermo ? 'checked' : ''} disabled aria-readonly="true">
          <span>${escapeHtmlAttr(acceptanceTermLabel)}</span>
        </label>
        ${pagamentoError ? '<div class="termos-error">Nenhum plano de pagamento disponível para esta combinação.</div>' : ''}
        ${termosError ? '<div class="termos-error">É obrigatório aceitar o termo para continuar.</div>' : ''}
      </div>`
              : ''
      }`;
    }

    function renderDadosPessoaisStep(): string {
        const est = Boolean(formData.estrangeiro);
        return `
      ${
          dadosPessoaisErrors.pagamento_estrangeiro
              ? `<div class="field-error" style="margin-bottom:0.75rem;line-height:1.45;">${escapeHtmlAttr(dadosPessoaisErrors.pagamento_estrangeiro)}</div>`
              : ''
      }
      <div class="field">
        <label>${est ? 'Passaporte' : 'CPF'}</label>
        <div class="field-row">
          <input type="text" id="${est ? 'passaporte' : 'cpf'}" placeholder="${est ? 'Número do passaporte' : '000.000.000-00'}" value="${escapeHtmlAttr(est ? String(formData.passaporte || '') : String(formData.cpf || ''))}" ${!est ? 'maxlength="14"' : ''}>
          <label class="check"><input type="checkbox" id="estrangeiro" ${est ? 'checked' : ''}> é estrangeiro?</label>
        </div>
        <div id="cpf-error" class="field-error" style="${dadosPessoaisErrors.cpf ? '' : 'display:none'}">${dadosPessoaisErrors.cpf || ''}</div>
        ${dadosPessoaisErrors.passaporte ? `<div class="field-error">${dadosPessoaisErrors.passaporte}</div>` : ''}
      </div>
      <div class="field">
        <label>Nome</label>
        <input type="text" id="nome" placeholder="Digite seu nome" value="${escapeHtmlAttr(String(formData.nome || ''))}" maxlength="100">
        ${dadosPessoaisErrors.nome ? `<div class="field-error">${dadosPessoaisErrors.nome}</div>` : ''}
      </div>
      <div class="field">
        <label class="check"><input type="checkbox" id="possuiNomeSocial" ${formData.possuiNomeSocial ? 'checked' : ''}> Possuo nome social<span class="hint-icon" title="Nome social se refere à designação pela qual a pessoa travesti ou transexual se identifica e é socialmente reconhecida.">i</span></label>
      </div>
      ${
          formData.possuiNomeSocial
              ? `
      <div class="field">
        <label>Nome Social</label>
        <input type="text" id="nomeSocial" placeholder="Digite seu nome social" value="${escapeHtmlAttr(String(formData.nomeSocial || ''))}" maxlength="100">
      </div>`
              : ''
      }
      <div class="field">
        <label>E-mail</label>
        <input type="email" id="email" placeholder="exemplo@exemplo.com" value="${escapeHtmlAttr(String(formData.email || ''))}" maxlength="250">
        ${dadosPessoaisErrors.email ? `<div class="field-error">${dadosPessoaisErrors.email}</div>` : ''}
      </div>
      <div class="field">
        <label>Telefone</label>
        <input type="tel" id="telefone" placeholder="(51) 99999-9999" value="${escapeHtmlAttr(String(formData.telefone || ''))}" maxlength="16">
        ${dadosPessoaisErrors.telefone ? `<div class="field-error">${dadosPessoaisErrors.telefone}</div>` : ''}
      </div>`;
    }

    function renderEnderecoStep(): string {
        const sid = Number(formData.state_id) || 0;
        const cid = Number(formData.city_id) || 0;
        return `
      <div class="field">
        <label>CEP</label>
        <input type="text" id="cep" placeholder="Digite seu CEP" value="${escapeHtmlAttr(String(formData.cep || ''))}" maxlength="9" ${cepLoading ? 'disabled' : ''}>
        ${cepLoading ? '<div class="cep-loading"><span class="spinner"></span>Buscando...</div>' : ''}
        ${enderecoErrors.cep ? `<div class="field-error">${enderecoErrors.cep}</div>` : ''}
      </div>
      <div class="field">
        <label>Endereço</label>
        <input type="text" id="endereco" placeholder="Digite seu endereço" value="${escapeHtmlAttr(String(formData.endereco || ''))}" maxlength="100">
        ${enderecoErrors.endereco ? `<div class="field-error">${enderecoErrors.endereco}</div>` : ''}
      </div>
      <div class="field-group endereco-numero-row">
        <div class="field endereco-numero-field">
          <label>Número</label>
          <input type="text" id="numero" placeholder="Digite seu número" value="${escapeHtmlAttr(String(formData.numero || ''))}" maxlength="8">
          ${enderecoErrors.numero ? `<div class="field-error">${enderecoErrors.numero}</div>` : ''}
        </div>
        <div class="field endereco-complemento-field">
          <label>Complemento <small>(opcional)</small></label>
          <input type="text" id="complemento" placeholder="Digite complemento" value="${escapeHtmlAttr(String(formData.complemento || ''))}" maxlength="60">
          ${enderecoErrors.complemento ? `<div class="field-error">${enderecoErrors.complemento}</div>` : ''}
        </div>
      </div>
      <div class="field">
        <label>Bairro</label>
        <input type="text" id="bairro" placeholder="Digite seu bairro" value="${escapeHtmlAttr(String(formData.bairro || ''))}" maxlength="80">
        ${enderecoErrors.bairro ? `<div class="field-error">${enderecoErrors.bairro}</div>` : ''}
      </div>
      <div class="field-group">
        <div class="field">
          <label>Estado</label>
          <select id="state_id">
            <option value="">Digite seu estado</option>
            ${statesList.map((s) => `<option value="${s.id}" ${sid === s.id ? 'selected' : ''}>${escapeHtmlAttr(s.name)}</option>`).join('')}
          </select>
          ${enderecoErrors.estado ? `<div class="field-error">${enderecoErrors.estado}</div>` : ''}
        </div>
        <div class="field">
          <label>Cidade</label>
          <select id="city_id">
            <option value="">Digite sua cidade</option>
            ${cidadesList.map((c) => `<option value="${c.id}" ${cid === c.id ? 'selected' : ''}>${escapeHtmlAttr(c.name)}</option>`).join('')}
          </select>
          ${enderecoErrors.cidade ? `<div class="field-error">${enderecoErrors.cidade}</div>` : ''}
        </div>
      </div>`;
    }

    const WIDGET_FIRST_INSTALLMENT_CALLOUT = `<p class="payment-first-installment-callout" role="note">
        O valor cobrado neste passo corresponde <strong>apenas à 1ª parcela</strong> do plano selecionado. As demais parcelas seguem o cronograma do plano.
      </p>`;

    /**
     * Banner do valor cobrado no cartão. Planos com charge_all cobram o total
     * parcelado no cartão (Nx); os demais cobram só a 1ª parcela.
     */
    function renderCardChargeBanner(): string {
        const plan = getCurrentPlan();
        if (!plan?.chargeAllInstallments) {
            return `<div class="payment-first-installment-banner">Valor cobrado corresponde à 1ª parcela da forma de pagamento selecionada.</div>`;
        }

        const planNet = getPlanNetPrice(plan);
        const couponDisc =
            formData.coupon_discount_type != null && formData.coupon_amount != null
                ? computeScholarshipDiscount(planNet, String(formData.coupon_discount_type), Number(formData.coupon_amount))
                : 0;
        const finalTotal = Math.max(0, planNet - couponDisc);
        const n = Math.max(1, parseInt(String(plan.installments ?? 1), 10) || 1);

        if (n <= 1) {
            return `<div class="payment-first-installment-banner">Você pagará o valor total de ${formatPrice(finalTotal)} no cartão.</div>`;
        }

        const perParcel = calcParcelamento(finalTotal, n).valorExibicao;
        return `<div class="payment-first-installment-banner">Você pagará o valor total de ${formatPrice(finalTotal)} em ${n}x de ${formatPrice(perParcel)} no cartão.</div>`;
    }

    function renderCartaoStep(): string {
        const err = cardTokenizeErrorMessage
            ? `<div class="field-error" style="margin-bottom:0.75rem;">${escapeHtmlAttr(cardTokenizeErrorMessage)}</div>`
            : '';
        return `
      ${renderCardChargeBanner()}
      <p class="payment-method-title">Informe os dados do seu cartão de crédito</p>
      <p class="payment-secure-tag">🔒 Ambiente seguro</p>
      ${err}
      <div class="field">
        <label>Número do cartão</label>
        <input type="text" id="card-number" inputmode="numeric" autocomplete="cc-number" placeholder="0000 0000 0000 0000" maxlength="19">
      </div>
      <div class="field">
        <label>Nome no cartão</label>
        <input type="text" id="card-holder" autocomplete="cc-name" placeholder="Conforme impresso no cartão" maxlength="64">
      </div>
      <div class="field-group card-expiry-row">
        <div class="field card-expiry-field">
          <label>Vencimento</label>
          <input type="text" id="card-expiry" inputmode="numeric" autocomplete="cc-exp" placeholder="MM/AAAA" maxlength="7">
          <input type="hidden" id="card-mm">
          <input type="hidden" id="card-yy">
        </div>
        <div class="field card-cvv-field">
          <label>CVV</label>
          <input type="text" id="card-cvv" inputmode="numeric" autocomplete="cc-csc" placeholder="000" maxlength="4">
        </div>
      </div>
      <p class="payment-email-note">Você receberá a confirmação da matrícula por e-mail.</p>
      <button type="button" class="btn-change-payment-method" id="btn-change-payment-method">← Trocar forma de pagamento</button>`;
    }

    const CHECKOUT_SUCCESS_ICON_SVG = `<div class="checkout-success-icon-wrap" aria-hidden="true">
        <svg class="checkout-success-icon" viewBox="0 0 72 72" width="72" height="72" focusable="false" aria-hidden="true">
          <g class="checkout-success-icon-content">
            <circle class="checkout-success-icon-circle" cx="36" cy="36" r="34" fill="#22c55e"/>
            <path class="checkout-success-check" pathLength="100" fill="none" stroke="#ffffff" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" d="M22 37l10 10 18-22"/>
          </g>
        </svg>
      </div>`;

    function renderCheckoutSuccess(): string {
        const inst = escapeHtmlAttr(institutionName ?? '');
        const title = inst ? `BOAS VINDAS À ${inst}!` : 'BOAS VINDAS!';
        const isBoletoPending = successVariant === 'boleto_generated';

        const statusTitle = isBoletoPending ? 'Boleto gerado!' : 'Pagamento confirmado!';

        const checkIconSize = isBoletoPending ? 56 : 40;

        const userLine = inst
            ? `Você receberá por e-mail, em breve, seu usuário ${inst} e o acesso ao Portal do Aluno.`
            : 'Você receberá por e-mail, em breve, seu usuário e o acesso ao Portal do Aluno.';

        const bodyHtml = isBoletoPending
            ? `<p class="success-body">Sua matrícula será confirmada em até 3 dias úteis, após a confirmação do pagamento.</p>
           <p class="success-body">${escapeHtmlAttr(userLine)}</p>`
            : `<p class="success-body">Agora você já faz parte${inst ? ` da ${inst}` : ' da instituição'}.</p>
           <p class="success-body">${escapeHtmlAttr(userLine)}</p>
           <p class="success-body">Fique atento ao seu e-mail e acompanhe mais informações sobre seu curso.</p>`;

        // Plano de cartão com cobrança integral não tem 2ª parcela a pagar no Portal.
        const chargeAllPaid = Boolean(getCurrentPlan()?.chargeAllInstallments) && !isBoletoPending;
        const attentionBox = chargeAllPaid
            ? `<div class="success-attention-box">O pagamento foi realizado integralmente no cartão. Não há parcelas pendentes.</div>`
            : `<div class="success-attention-box">Ao acessar o Portal do Aluno você deverá escolher o método de pagamento da 2ª parcela e validar suas informações financeiras.</div>`;

        return `
      <div class="success-screen" role="status" aria-live="polite">
        <h2 class="success-title">${escapeHtmlAttr(title)}</h2>
        <div class="success-check-icon" aria-hidden="true">
          <svg viewBox="0 0 72 72" width="${checkIconSize}" height="${checkIconSize}">
            <circle cx="36" cy="36" r="34" fill="none" stroke="#fff" stroke-width="3"/>
            <path fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" d="M22 37l10 10 18-22"/>
          </svg>
        </div>
        <p class="success-status-title">${escapeHtmlAttr(statusTitle)}</p>
        ${bodyHtml}
        ${attentionBox}
      </div>`;
    }

    function renderPixReceivedScreen(): string {
        return `
      <div class="pix-received-inner">
        ${CHECKOUT_SUCCESS_ICON_SVG}
        <p class="pix-received-title">Recebemos o pagamento</p>
        <p class="pix-received-sub">Confirmando sua inscrição…</p>
      </div>`;
    }

    function handleConcluirPayment(): void {
        stopPaymentPoll();
        if (paymentUi === 'boleto') {
            successVariant = 'boleto_generated';
        } else if (paymentSimulation) {
            // No simulado é aqui que o pagamento é registrado — até este clique a
            // inscrição segue não paga e dá para voltar e trocar de forma.
            if (lastEnrollmentId != null) {
                void confirmSimulatedPayment(lastEnrollmentId);

                return;
            }
            successVariant = 'payment_confirmed';
        } else {
            successVariant = 'pix_pending';
        }
        enrollmentSuccessId = lastEnrollmentId;
        paymentUi = 'none';
        pixPaymentData = null;
        boletoPaymentData = null;
        void render();
    }

    function renderPaymentAwaitingScreen(): string {
        const pix = paymentUi === 'pix';

        const firstInstallmentBanner = `
      <div class="payment-first-installment-banner">Valor cobrado corresponde à 1ª parcela da forma de pagamento selecionada.</div>`;

        const secureTag = `<p class="payment-secure-tag">🔒 Ambiente seguro</p>`;
        const btnConcluir = `<button type="button" class="btn-primary" id="btn-concluir-payment">Concluir</button>`;
        const btnChangePayment = `<button type="button" class="btn-change-payment-method" id="btn-change-payment-method">← Trocar forma de pagamento</button>`;

        if (pix) {
            const qrImg = pixPaymentData?.qr_code_url
                ? `<div class="pix-qr-wrap"><img src="${escapeHtmlAttr(String(pixPaymentData.qr_code_url))}" alt="QR Code PIX" class="pix-qr-img"></div>`
                : '';
            const m = Math.floor(pixCountdownSeconds / 60)
                .toString()
                .padStart(2, '0');
            const s = (pixCountdownSeconds % 60).toString().padStart(2, '0');
            const copyCode = pixPaymentData?.qr_code
                ? `<div class="payment-code-block" id="widget-pix-copy-field-wrap">
                <div class="payment-code-text" id="widget-pix-copy-field">${escapeHtmlAttr(String(pixPaymentData.qr_code))}</div>
               </div>
               <button type="button" class="btn-copy-code" id="btn-copy-pix-code">Copiar código Pix</button>`
                : '';
            // Homologação: a cobrança é fictícia e se confirma sozinha — dizer isso
            // na tela evita alguém tentar pagar o código ou reportar como bug.
            const simulationNotice = paymentSimulation
                ? `<div class="payment-first-installment-banner">Ambiente de homologação: cobrança simulada, com confirmação automática em alguns segundos. Não pague este código.</div>`
                : '';
            return `
          ${firstInstallmentBanner}
          ${simulationNotice}
          <p class="payment-method-title">Código Pix gerado!</p>
          <p class="payment-hint">Escaneie o código QR com o aplicativo do seu banco ou copie o código Pix.</p>
          ${qrImg}
          <p class="pix-countdown" id="pix-countdown">${m}:${s}</p>
          ${secureTag}
          ${copyCode}
          <p class="payment-email-note">Você receberá a confirmação da matrícula por e-mail.</p>
          ${btnChangePayment}
          ${btnConcluir}`;
        }

        const boletoLine = boletoPaymentData?.line
            ? `<div class="payment-code-block">
             <div class="payment-code-text" id="widget-boleto-line">${escapeHtmlAttr(String(boletoPaymentData.line))}</div>
           </div>
           <button type="button" class="btn-copy-code" id="btn-copy-boleto-line">Copiar linha digitável</button>`
            : '';
        const boletoPdf = boletoPaymentData?.pdf_url
            ? `<a href="${escapeHtmlAttr(String(boletoPaymentData.pdf_url))}" target="_blank" rel="noopener noreferrer" class="btn-copy-code btn-outline">Baixar boleto em PDF</a>`
            : '';

        return `
      ${firstInstallmentBanner}
      <p class="payment-method-title">Boleto gerado!</p>
      <p class="payment-hint">Copie a linha digitável ou baixe o boleto para realizar o pagamento.</p>
      ${boletoLine}
      ${boletoPdf}
      ${secureTag}
      <p class="payment-email-note">Você receberá a confirmação da matrícula por e-mail após o pagamento.</p>
      ${btnChangePayment}
      ${btnConcluir}`;
    }

    function renderPaymentErrorScreen(): string {
        const intro = paymentErrorIntro ? `<p class="payment-error-intro">${escapeHtmlAttr(paymentErrorIntro)}</p>` : '';
        const detail = paymentFailureMessage
            ? `<p class="payment-error-detail">${escapeHtmlAttr(paymentFailureMessage)}</p>`
            : '<p class="payment-error-detail">Não foi possível concluir o pagamento.</p>';
        const contact = paymentErrorContactHtml ? `<div class="payment-error-contact">${paymentErrorContactHtml}</div>` : '';
        return `
      <div class="payment-error-screen">
        <h3>Pagamento</h3>
        ${intro}
        ${detail}
        ${contact}
        <div class="payment-error-actions">
          <button type="button" class="primary" id="btn-payment-retry">Tentar novamente</button>
          <button type="button" class="secondary" id="btn-payment-change-plan">Trocar forma de pagamento</button>
        </div>
      </div>`;
    }

    async function fetchCepFlow(cepRaw: string): Promise<void> {
        const digits = String(cepRaw).replace(/\D/g, '');
        if (digits.length !== 8) return;
        collectStepData();
        formData.cep = maskCep(digits);
        cepLoading = true;
        render();
        try {
            const data = await fetchCep(apiBase, digits);
            formData.postal_code = data.postal_code;
            formData.endereco = (data.street as string) || '';
            formData.bairro = (data.neighborhood as string) || '';
            formData.country_id = (data.country_id as number) || defaultCountryId;
            formData.state_id = (data.state_id as number) || null;
            formData.city_id = (data.city_id as number) || null;
            if (formData.cep && typeof data.postal_code === 'string') {
                formData.cep = String(data.postal_code);
            }
            if (formData.state_id) {
                await loadCitiesForState(Number(formData.state_id));
            }
        } catch {
            /* mantém campos manuais */
        } finally {
            cepLoading = false;
            render();
        }
    }

    async function submitCardPaymentFlow(): Promise<void> {
        if (!pagarmePublicKey) {
            checkoutErrorMessage = 'Pagamento com cartão não está disponível. Contacte a instituição.';
            await render();
            return;
        }
        if (!lastEnrollmentId) {
            checkoutErrorMessage = 'Sessão inválida. Volte e preencha novamente.';
            await render();
            return;
        }
        collectStepData();
        const num = (shadow.getElementById('card-number') as HTMLInputElement | null)?.value?.replace(/\D/g, '') || '';
        const holder = (shadow.getElementById('card-holder') as HTMLInputElement | null)?.value?.trim() || '';
        const mm = parseInt(String((shadow.getElementById('card-mm') as HTMLInputElement | null)?.value || '').trim(), 10);
        const yyRaw = String((shadow.getElementById('card-yy') as HTMLInputElement | null)?.value || '').trim();
        const yy = yyRaw.length === 2 ? 2000 + parseInt(yyRaw, 10) : parseInt(yyRaw, 10);
        const cvv = (shadow.getElementById('card-cvv') as HTMLInputElement | null)?.value?.trim() || '';
        if (num.length < 13) {
            cardTokenizeErrorMessage = 'Número do cartão inválido.';
            await render();
            return;
        }
        if (holder.length < 3) {
            cardTokenizeErrorMessage = 'Informe o nome como está no cartão.';
            await render();
            return;
        }
        if (!Number.isFinite(mm) || mm < 1 || mm > 12) {
            cardTokenizeErrorMessage = 'Mês de validade inválido.';
            await render();
            return;
        }
        if (!Number.isFinite(yy) || yy < 2000) {
            cardTokenizeErrorMessage = 'Ano de validade inválido.';
            await render();
            return;
        }
        if (cvv.length < 3) {
            cardTokenizeErrorMessage = 'CVV inválido.';
            await render();
            return;
        }
        submitting = true;
        cardTokenizeErrorMessage = null;
        checkoutErrorMessage = null;
        await render();
        try {
            const token = await createPagarmeCardToken(pagarmePublicKey, {
                number: num,
                holder_name: holder,
                exp_month: mm,
                exp_year: yy,
                cvv,
            });
            const data = await postWidgetPayment(apiBase, lastEnrollmentId, {
                session_id: sessionId,
                payment_method: 'cartao',
                card_token: token,
            });
            const st = String(data.charge_status || data.card?.status || '');
            if (['paid', 'captured'].includes(st)) {
                successVariant = 'payment_confirmed';
                enrollmentSuccessId = lastEnrollmentId;
                paymentUi = 'none';
            } else {
                paymentFailureMessage = data.card?.refused_reason || 'Pagamento não autorizado.';
                paymentUi = 'payment_error';
            }
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Erro ao processar o cartão.';
            const isConnectionError =
                /Não foi possível conectar ao serviço de pagamento \(Pagar\.me\)/i.test(msg) || /^Não foi possível conectar ao servidor\./i.test(msg);
            if (isConnectionError) {
                cardTokenizeErrorMessage = msg;
                paymentUi = 'none';
            } else {
                paymentFailureMessage = msg;
                paymentUi = 'payment_error';
            }
        } finally {
            submitting = false;
        }
        await render();
    }

    let shadowDelegatedBound = false;

    function attachListeners(): void {
        shadow.querySelectorAll('.btn-tipo[data-admission]').forEach((btn) => {
            btn.addEventListener('click', () => {
                collectStepData();
                pagamentoError = false;
                const id = (btn as HTMLButtonElement).dataset.admission;
                formData.tipoIngresso = id ? `at_${id}` : String(formData.tipoIngresso);
                ensureFormaPagamentoForTipoIngresso();
                ensurePaymentPlanForCurrentMethod();
                syncPlanFromFormData();
                render();
            });
        });
        shadow.querySelectorAll('.payment-option[data-pag]').forEach((el) => {
            el.addEventListener('click', () => {
                collectStepData();
                pagamentoError = false;
                formData.formaPagamento = (el as HTMLElement).dataset.pag;
                ensurePaymentPlanForCurrentMethod();
                syncPlanFromFormData();
                render();
            });
        });
        const selectPlanoPagamento = shadow.getElementById('pagamento_plano_id');
        if (selectPlanoPagamento) {
            selectPlanoPagamento.addEventListener('change', () => {
                collectStepData();
                syncPlanFromFormData();
                void render();
            });
        }
        const cbCupom = shadow.getElementById('cupom');
        if (cbCupom) {
            cbCupom.addEventListener('change', () => {
                collectStepData();
                if (!(cbCupom as HTMLInputElement).checked) {
                    couponError = null;
                    formData.coupon_id = null;
                    formData.coupon_discount_type = null;
                    formData.coupon_amount = null;
                    formData.coupon_code_display = '';
                    formData.coupon_scholarship_name = '';
                    formData.codigoCupom = '';
                }
                pagamentoError = false;
                termosError = false;
                void render();
            });
        }
        const btnAplicarCupom = shadow.getElementById('btn-aplicar-cupom');
        if (btnAplicarCupom) {
            btnAplicarCupom.addEventListener('click', () => {
                void applyCouponFlow();
            });
        }

        shadow.querySelectorAll('.payment-method-card[data-pag]').forEach((el) => {
            el.addEventListener('click', (e) => {
                if ((e.target as HTMLElement).closest('.installment-options')) return;
                collectStepData();
                pagamentoError = false;
                formData.formaPagamento = (el as HTMLElement).dataset.pag;
                ensurePaymentPlanForCurrentMethod();
                syncPlanFromFormData();
                void render();
            });
        });

        shadow.querySelectorAll<HTMLElement>('.installment-radio[data-plan-id]').forEach((label) => {
            label.addEventListener('click', (e) => {
                e.stopPropagation();
                const pid = parseInt(label.dataset.planId ?? '', 10);
                if (!isNaN(pid)) {
                    formData.payment_plan_id = pid;
                    syncPlanFromFormData();
                    void render();
                }
            });
        });

        const couponAccordionBtn = shadow.getElementById('btn-coupon-accordion');
        if (couponAccordionBtn) {
            couponAccordionBtn.addEventListener('click', () => {
                formData.couponAccordionOpen = !Boolean(formData.couponAccordionOpen);
                void render();
            });
        }

        const inputCoupon = shadow.getElementById('codigoCupom');
        if (inputCoupon) {
            inputCoupon.addEventListener('blur', () => {
                collectStepData();
                const code = String(formData.codigoCupom || '').trim();
                if (code && code !== String(formData.coupon_code_display || '')) {
                    void applyCouponFlow();
                }
            });
            inputCoupon.addEventListener('keydown', (e) => {
                if ((e as KeyboardEvent).key === 'Enter') void applyCouponFlow();
            });
        }
        const cbNomeSocial = shadow.getElementById('possuiNomeSocial');
        if (cbNomeSocial) {
            cbNomeSocial.addEventListener('change', () => {
                collectStepData();
                formData.possuiNomeSocial = (cbNomeSocial as HTMLInputElement).checked;
                if (!(cbNomeSocial as HTMLInputElement).checked) formData.nomeSocial = '';
                render();
            });
        }
        const cbEstrangeiro = shadow.getElementById('estrangeiro');
        if (cbEstrangeiro) {
            cbEstrangeiro.addEventListener('change', () => {
                collectStepData();
                formData.estrangeiro = (cbEstrangeiro as HTMLInputElement).checked;
                if ((cbEstrangeiro as HTMLInputElement).checked) formData.cpf = '';
                else formData.passaporte = '';
                dadosPessoaisErrors = {};
                render();
            });
        }
        const inputCpf = shadow.getElementById('cpf');
        if (inputCpf) {
            inputCpf.addEventListener('input', () => {
                (inputCpf as HTMLInputElement).value = maskCpf((inputCpf as HTMLInputElement).value);
                delete dadosPessoaisErrors.cpf;
                const errEl = shadow.getElementById('cpf-error');
                if (errEl) {
                    errEl.textContent = '';
                    errEl.style.display = 'none';
                }
            });
            inputCpf.addEventListener('blur', () => {
                collectStepData();
                // CRÍTICO: NÃO chamar render() aqui. Quando o user clica no
                // btn-next, o blur dispara antes do click chegar; um render
                // destrói o btn-next e o click é descartado (era a causa
                // raiz REAL do "preciso clicar 2x"). A validação inline
                // de CPF é feita manipulando o errEl diretamente, sem
                // re-render — o goNext consolida a validação no clique.
                const errEl = shadow.getElementById('cpf-error');
                if (!formData.estrangeiro) {
                    const digits = String(formData.cpf || '').replace(/\D/g, '');
                    let msg = '';
                    if (digits.length === 11) {
                        if (!isValidCpf(String(formData.cpf))) {
                            msg = 'CPF inválido.';
                            dadosPessoaisErrors.cpf = msg;
                        } else {
                            delete dadosPessoaisErrors.cpf;
                        }
                    } else if (digits.length > 0) {
                        msg = 'CPF inválido. Informe os 11 dígitos.';
                        dadosPessoaisErrors.cpf = msg;
                    } else {
                        delete dadosPessoaisErrors.cpf;
                    }
                    if (errEl) {
                        errEl.textContent = msg;
                        errEl.style.display = msg ? '' : 'none';
                    }
                }
            });
        }
        const inputTelefone = shadow.getElementById('telefone');
        if (inputTelefone) {
            inputTelefone.addEventListener('input', () => {
                (inputTelefone as HTMLInputElement).value = maskTelefone((inputTelefone as HTMLInputElement).value);
            });
        }
        const inputCep = shadow.getElementById('cep');
        if (inputCep) {
            inputCep.addEventListener('input', () => {
                (inputCep as HTMLInputElement).value = maskCep((inputCep as HTMLInputElement).value);
                const digits = (inputCep as HTMLInputElement).value.replace(/\D/g, '');
                if (digits.length === 8 && !cepLoading) void fetchCepFlow((inputCep as HTMLInputElement).value);
            });
        }
        const selectState = shadow.getElementById('state_id');
        if (selectState) {
            selectState.addEventListener('change', () => {
                collectStepData();
                const v = parseInt((selectState as HTMLSelectElement).value, 10);
                formData.state_id = Number.isNaN(v) ? null : v;
                formData.city_id = null;
                void loadCitiesForState(v).then(() => render());
            });
        }
        const btnAddNomeSocial = shadow.getElementById('btn-add-nome-social');
        if (btnAddNomeSocial) {
            btnAddNomeSocial.addEventListener('click', () => {
                collectStepData();
                formData.possuiNomeSocial = true;
                void render();
            });
        }
        const btnRemoveNomeSocial = shadow.getElementById('btn-remove-nome-social');
        if (btnRemoveNomeSocial) {
            btnRemoveNomeSocial.addEventListener('click', () => {
                collectStepData();
                formData.possuiNomeSocial = false;
                formData.nomeSocial = '';
                void render();
            });
        }

        // NÃO chamamos checkCpfInfo no blur do CPF.
        //
        // Histórico: o blur→checkCpfInfo causava re-render durante o clique
        // no btn-next (o blur dispara enquanto o click ainda está em fly,
        // destruindo o botão e descartando o evento). Tentativas anteriores:
        //   - relatedTarget no FocusEvent → vem null em mobile (touch).
        //   - flag nextClickPending setada por pointerdown → falha quando
        //     a ordem dos eventos é blur→pointerdown (raro mas reproduzível
        //     em iOS Safari em alguns toques rápidos).
        //
        // Decisão final: a checagem só roda em goNext, com spinner no
        // botão. UX: o badge "Desconto ex-aluno" e o aviso "já matriculado"
        // só aparecem depois que o aluno clica Matricule-se — pequena perda
        // de UX em troca de zero race. Vide goNext em STEPS.DADOS_BASICOS.

        const inputCardExpiry = shadow.getElementById('card-expiry') as HTMLInputElement | null;
        if (inputCardExpiry) {
            const mmEl = shadow.getElementById('card-mm') as HTMLInputElement | null;
            const yyEl = shadow.getElementById('card-yy') as HTMLInputElement | null;
            inputCardExpiry.addEventListener('input', () => {
                const digits = inputCardExpiry.value.replace(/\D/g, '').slice(0, 6);
                const mm = digits.slice(0, 2);
                const yy = digits.slice(2, 6);
                inputCardExpiry.value = yy ? `${mm}/${yy}` : mm;
                if (mmEl) mmEl.value = mm;
                if (yyEl) yyEl.value = yy;
            });
        }

        if (!shadowDelegatedBound) {
            shadowDelegatedBound = true;
            let saveTimeout: ReturnType<typeof setTimeout> | null = null;
            shadow.addEventListener('input', () => {
                if (saveTimeout) clearTimeout(saveTimeout);
                saveTimeout = setTimeout(() => collectStepData(), 400);
            });
            shadow.addEventListener('change', () => collectStepData());
        }
    }

    function goBack(): void {
        collectStepData();
        checkoutErrorMessage = null;
        cardTokenizeErrorMessage = null;

        if (formData.showAlreadyEnrolledScreen) {
            formData.showAlreadyEnrolledScreen = false;
            void render();
            return;
        }

        if (paymentUi === 'payment_error') {
            paymentUi = 'none';
            paymentFailureMessage = null;
            currentStep = STEPS.PLANO;
            void trackStep();
            void render();
            return;
        }
        if (paymentUi === 'pix' || paymentUi === 'boleto') {
            stopPaymentPoll();
            paymentUi = 'none';
            pixPaymentData = null;
            boletoPaymentData = null;
            pixAwaitStartedAt = null;
            currentStep = STEPS.PLANO;
            void trackStep();
            void render();
            return;
        }
        if (currentStep === STEPS.ENDERECO) {
            currentStep = STEPS.DADOS_BASICOS;
        } else if (currentStep === STEPS.PLANO) {
            currentStep = STEPS.ENDERECO;
        } else if (currentStep === STEPS.PAGAMENTO_EXEC) {
            currentStep = STEPS.PLANO;
        }
        void trackStep();
        void render();
    }

    function dlEmail(): string {
        return String(formData.email || '').trim();
    }

    function dlNome(): string {
        return String(formData.nome || '').trim();
    }

    function dlTelefone(): string {
        return String(formData.telefone || '').trim();
    }

    function dlCep(): string {
        return String(formData.cep || '').trim();
    }

    function dlCodCurso(): string {
        return String(offerPayload?.offer?.code ?? '').trim();
    }

    /**
     * "Valor total do curso" (spec). Preço cheio do plano default da oferta —
     * referência inicial enviada no PASSO 01, antes de o aluno escolher plano/cupom.
     * O valor real cobrado/contratado sai depois em transactionValue (PASSO 04).
     */
    function dlAmount(): number {
        const plan = getCurrentPlan();
        return plan ? parsePrice(plan.price ?? 0) : 0;
    }

    /**
     * "Vinculo" do aluno (PASSO 02). Egresso quando o CPF foi identificado como
     * ex-aluno (cpfAlumniInfo.is_alumni), caso contrário publico_geral. Outros
     * admission_types eventualmente cadastrados caem em publico_geral.
     */
    function dlVinculo(): string {
        const info = formData.cpfAlumniInfo as { is_alumni: boolean } | null;
        return info?.is_alumni ? 'Egresso' : 'publico_geral';
    }

    /**
     * Forma de pagamento escolhida pelo aluno no E3 (pix, boleto ou cartao).
     */
    function dlFormaPagamento(): string {
        return String(formData.formaPagamento || '').trim();
    }

    /**
     * Valor da transação contratada — total líquido pós-desconto-do-plano e
     * pós-cupom. Reflete o LTV do aluno (o que ele se compromete a pagar pelo
     * curso), independente de parcelamento (cobramos 1ª parcela no gateway, o
     * restante segue cronograma do TOTVS).
     */
    function dlTransactionValue(): number {
        const plan = getCurrentPlan();
        if (!plan) return 0;
        const planNet = getPlanNetPrice(plan);
        const couponDisc =
            formData.coupon_discount_type != null && formData.coupon_amount != null
                ? computeScholarshipDiscount(planNet, String(formData.coupon_discount_type), Number(formData.coupon_amount))
                : 0;
        return Math.max(0, planNet - couponDisc);
    }

    function dlPush(event: string, payload: Record<string, unknown>): void {
        if (!datalayerEventsEnabled) return;
        pushDataLayer(event, payload);
    }

    async function goNext(): Promise<void> {
        if (advancing || submitting) return;
        collectStepData();

        if (currentStep === STEPS.DADOS_BASICOS) {
            // Único ponto onde checkCpfInfo é chamado: mostra spinner no
            // botão, await result, decide se bloqueia (egresso/matrícula) ou
            // prossegue. lastCheckedKey torna idempotente para cliques
            // múltiplos com a mesma identificação (e-mail + CPF).
            const cpfDigits = String(formData.cpf || '').replace(/\D/g, '');
            const checkEmail = String(formData.email || '').trim();
            const checkKey = candidateCheckKey(checkEmail, cpfDigits, Boolean(formData.estrangeiro));
            if (checkKey !== null && lastCheckedKey !== checkKey) {
                advancing = true;
                await render();
                try {
                    await checkCpfInfo(checkEmail, String(formData.cpf || ''));
                } finally {
                    advancing = false;
                }
                await render();
            }

            if (formData.cpfIsEnrolled || formData.cpfHasMatricula) {
                // Matrícula (TOTVS) sempre exibe a tela de bloqueio com o HTML
                // configurado; inscrição paga local mantém o atalho de contato.
                formData.blockReason = formData.cpfHasMatricula ? 'matricula' : 'enrolled';
                if (formData.blockReason === 'enrolled' && contactUrl) {
                    window.open(contactUrl, '_blank');
                } else {
                    formData.showAlreadyEnrolledScreen = true;
                    void render();
                }
                return;
            }
            dadosPessoaisErrors = validateDadosPessoais(formData);
            if (Object.keys(dadosPessoaisErrors).length > 0) {
                await render();
                return;
            }
            dadosPessoaisErrors = {};
            syncPlanFromFormData();
            advancing = true;
            await render();
            try {
                if (!(await syncProgress('dados_basicos'))) return;
                dlPush('lato_venda_primeiro_passo', {
                    email: dlEmail(),
                    nome: dlNome(),
                    cod_curso: dlCodCurso(),
                    amount: dlAmount(),
                });
                currentStep = STEPS.ENDERECO;
                void trackStep();
                await ensureStates();
            } finally {
                advancing = false;
            }
            await render();
            return;
        }

        if (currentStep === STEPS.ENDERECO) {
            enderecoErrors = validateEnderecoProd(formData);
            if (Object.keys(enderecoErrors).length > 0) {
                await render();
                return;
            }
            enderecoErrors = {};
            advancing = true;
            await render();
            try {
                if (!(await syncProgress('dados_endereco'))) return;
                dlPush('lato_venda_segundo_passo', {
                    email: dlEmail(),
                    nome: dlNome(),
                    telefone: dlTelefone(),
                    Vinculo: dlVinculo(),
                    CEP: dlCep(),
                });
                currentStep = STEPS.PLANO;
                void trackStep();
            } finally {
                advancing = false;
            }
            await render();
            return;
        }

        if (currentStep === STEPS.PLANO) {
            if (!getCurrentPlan()) {
                pagamentoError = true;
                await render();
                return;
            }
            if (acceptanceTermPdfUrl && !formData.aceiteTermo) {
                showTermsModal = true;
                termsScrolledToEnd = false;
                if (termsPdfObserver) {
                    termsPdfObserver.disconnect();
                    termsPdfObserver = null;
                }
                await render();
                void initTermsPdf();
                return;
            }
            pagamentoError = false;
            syncPlanFromFormData();
            advancing = true;
            await render();
            try {
                if (!(await syncProgress('pagamento'))) return;
                dlPush('lato_venda_terceiro_passo', {
                    nome: dlNome(),
                    email: dlEmail(),
                    forma_pagamento: dlFormaPagamento(),
                });
            } finally {
                advancing = false;
            }
            submitting = true;
            checkoutErrorMessage = null;
            paymentFailureMessage = null;
            await render();
            try {
                dlPush('lato_venda_go_checkout', {
                    email: dlEmail(),
                    transactionValue: dlTransactionValue(),
                });
                const id = await registerEnrollmentOnly();
                lastEnrollmentId = id;
                if (paymentSimulation) {
                    await startSimulatedPayment();
                } else if (!pagarmePublicKey) {
                    successVariant = 'payment_confirmed';
                    enrollmentSuccessId = id;
                    stopPaymentPoll();
                } else if (String(formData.formaPagamento) === 'cartao') {
                    currentStep = STEPS.PAGAMENTO_EXEC;
                    void trackStep();
                    // Entrou na tela de digitar o cartão: dispara o evento de
                    // pagamento pendente no Rubeus antes da cobrança (best-effort).
                    void postEnrollmentPaymentPending(apiBase, id, sessionId);
                } else {
                    await startPaymentForEnrollment(id, String(formData.formaPagamento) === 'pix' ? 'pix' : 'boleto');
                }
            } catch (e) {
                checkoutErrorMessage = e instanceof Error ? e.message : 'Erro ao registrar inscrição.';
            } finally {
                submitting = false;
            }
            await render();
            return;
        }

        if (currentStep === STEPS.PAGAMENTO_EXEC) {
            void submitCardPaymentFlow();
        }
    }

    async function trackStep(): Promise<void> {
        const stepName = STEP_NAMES[currentStep] ?? 'unknown';
        await postWidgetEvent(apiBase, {
            event: 'step_view',
            session_id: sessionId,
            offer_id: offerIdParam,
            theme_id: themeIdParam,
            payload: { step: currentStep, stepName },
        });
    }

    async function render(): Promise<void> {
        // Preserva o scroll da página: substituir shadow.innerHTML remove o
        // elemento focado e o browser pode deslocar a página para o topo.
        const savedScrollY = window.scrollY;
        const restoreScroll = (): void => {
            if (window.scrollY !== savedScrollY) {
                window.scrollTo({ top: savedScrollY, behavior: 'instant' as ScrollBehavior });
            }
        };
        if (enrollmentSuccessId != null) {
            stopPaymentPoll();
            shadow.innerHTML = `
        <style>${getThemeVarsCss(theme)}${WIDGET_STYLES}</style>
        <div class="widget widget-success">${renderCheckoutSuccess()}</div>`;
            requestAnimationFrame(restoreScroll);
            return;
        }

        if (paymentUi === 'pix_received') {
            shadow.innerHTML = `
        <style>${getThemeVarsCss(theme)}${WIDGET_STYLES}</style>
        <div class="widget widget-pix-received" role="status" aria-live="polite" data-offer-id="${escapeHtmlAttr(String(offerNumericId))}" data-theme-id="${escapeHtmlAttr(themeIdParam)}">
          <h2>${escapeHtmlAttr(widgetTitle)}</h2>
          <div id="step-content">${renderPixReceivedScreen()}</div>
        </div>`;
            requestAnimationFrame(restoreScroll);
            return;
        }

        if (paymentUi === 'pix' || paymentUi === 'boleto') {
            const awaitingProgress = renderProgressBar(STEPS.PAGAMENTO_EXEC);
            shadow.innerHTML = `
        <style>${getThemeVarsCss(theme)}${WIDGET_STYLES}</style>
        <div class="widget widget-payment-awaiting">
          ${awaitingProgress}
          <h2>REALIZE O PAGAMENTO</h2>
          <div id="step-content">${renderPaymentAwaitingScreen()}</div>
        </div>`;

            const btnPix = shadow.getElementById('btn-copy-pix-code');
            if (btnPix instanceof HTMLButtonElement) {
                btnPix.addEventListener('click', () => {
                    const el = shadow.getElementById('widget-pix-copy-field');
                    const raw = el?.textContent?.trim() ?? '';
                    void copyTextToClipboard(raw).then((ok) => {
                        if (ok) flashButtonLabel(btnPix, 'Copiado!');
                    });
                });
            }
            const btnBol = shadow.getElementById('btn-copy-boleto-line');
            if (btnBol instanceof HTMLButtonElement) {
                btnBol.addEventListener('click', () => {
                    const el = shadow.getElementById('widget-boleto-line');
                    const raw = el?.textContent?.trim() ?? '';
                    void copyTextToClipboard(raw).then((ok) => {
                        if (ok) flashButtonLabel(btnBol, 'Copiado!');
                    });
                });
            }
            const btnConcluir = shadow.getElementById('btn-concluir-payment');
            if (btnConcluir) {
                btnConcluir.addEventListener('click', () => handleConcluirPayment());
            }
            const btnChange = shadow.getElementById('btn-change-payment-method');
            if (btnChange) {
                btnChange.addEventListener('click', () => goBack());
            }
            requestAnimationFrame(restoreScroll);
            return;
        }

        if (paymentUi === 'payment_error') {
            shadow.innerHTML = `
        <style>${getThemeVarsCss(theme)}${WIDGET_STYLES}</style>
        <div class="widget widget-payment-error">
          <h2>${escapeHtmlAttr(widgetTitle)}</h2>
          <div id="step-content">${renderPaymentErrorScreen()}</div>
        </div>`;
            const retry = shadow.getElementById('btn-payment-retry');
            if (retry) {
                retry.addEventListener('click', () => {
                    void (async () => {
                        paymentUi = 'none';
                        paymentFailureMessage = null;
                        if (lastPixBoletoMethod && lastEnrollmentId) {
                            await startPaymentForEnrollment(lastEnrollmentId, lastPixBoletoMethod);
                        } else {
                            currentStep = STEPS.PAGAMENTO_EXEC;
                            await render();
                        }
                    })();
                });
            }
            const ch = shadow.getElementById('btn-payment-change-plan');
            if (ch) {
                ch.addEventListener('click', () => {
                    stopPaymentPoll();
                    paymentUi = 'none';
                    paymentFailureMessage = null;
                    currentStep = STEPS.PLANO;
                    void trackStep();
                    void render();
                });
            }
            requestAnimationFrame(restoreScroll);
            return;
        }

        const isEnrolledStep = (Boolean(formData.cpfIsEnrolled) || Boolean(formData.cpfHasMatricula)) && currentStep === STEPS.DADOS_BASICOS;
        const nextLabel = submitting
            ? 'Enviando...'
            : isEnrolledStep
              ? 'Tire suas dúvidas'
              : currentStep === STEPS.DADOS_BASICOS
                ? 'Matricule-se'
                : currentStep === STEPS.PLANO
                  ? 'Pagar'
                  : currentStep === STEPS.PAGAMENTO_EXEC
                    ? 'Pagar'
                    : 'Continuar';

        const widgetStepClass =
            currentStep === STEPS.PAGAMENTO_EXEC
                ? 'step-pagamento-exec'
                : currentStep === STEPS.ENDERECO
                  ? 'step-endereco'
                  : currentStep === STEPS.DADOS_BASICOS
                    ? 'step-dados-basicos'
                    : currentStep === STEPS.PLANO
                      ? 'step-plano'
                      : '';

        const subtitle = STEP_SUBTITLES[currentStep] ?? '';
        const progressHtml = renderProgressBar(currentStep);
        const showingAlreadyEnrolled = Boolean(formData.showAlreadyEnrolledScreen) && currentStep === STEPS.DADOS_BASICOS;
        const stepHeadingTitle = showingAlreadyEnrolled
            ? institutionName
                ? `BOAS VINDAS À ${institutionName.toUpperCase()}!`
                : 'BOAS VINDAS!'
            : currentStep === STEPS.PAGAMENTO_EXEC
              ? 'REALIZE O PAGAMENTO'
              : 'MATRICULE-SE';

        // Bloqueio por matrícula já efetivada (TEMMATRICULA=S): exibe apenas a
        // mensagem cadastrada, sem botão de ação no rodapé.
        const matriculaBlockScreen = showingAlreadyEnrolled && formData.blockReason === 'matricula';
        const backBtnHtml =
            currentStep > STEPS.DADOS_BASICOS && currentStep !== STEPS.PAGAMENTO_EXEC && paymentUi === 'none'
                ? `<button type="button" class="secondary" id="btn-back" ${advancing || submitting ? 'disabled' : ''}>Voltar</button>`
                : '';
        const actionsHtml = matriculaBlockScreen
            ? ''
            : `<div class="actions">
          ${backBtnHtml}
          <button type="button" class="primary btn-next" id="btn-next" ${advancing || submitting ? 'disabled' : ''}>
            ${advancing ? '<span class="btn-spinner" aria-hidden="true"></span>' : ''}
            <span>${advancing ? 'Carregando…' : escapeHtmlAttr(nextLabel)}</span>
          </button>
        </div>`;

        const termModalHtml =
            showTermsModal && acceptanceTermPdfUrl
                ? `<div class="terms-modal-overlay" id="terms-modal-overlay">
                <div class="terms-modal">
                  <div class="terms-pdf-scroll" id="terms-pdf-scroll">
                    <div id="terms-pdf-pages">
                      <p class="terms-pdf-loading" id="terms-pdf-loading">Carregando PDF…</p>
                    </div>
                  </div>
                  <div class="terms-modal-footer">
                    <p class="terms-modal-label">${escapeHtmlAttr(acceptanceTermLabel)}</p>
                    <button type="button" class="primary terms-modal-confirm" id="btn-terms-accept"${termsScrolledToEnd ? '' : ' disabled'}>SIM</button>
                  </div>
                  ${termsScrolledToEnd ? '' : '<p class="terms-scroll-hint" id="terms-scroll-hint">Role até o fim para aceitar ↓</p>'}
                </div>
              </div>`
                : '';

        shadow.innerHTML = `
      <style>${getThemeVarsCss(theme)}${WIDGET_STYLES}</style>
      ${termModalHtml}
      <div class="widget ${widgetStepClass}" data-offer-id="${escapeHtmlAttr(String(offerNumericId))}" data-theme-id="${escapeHtmlAttr(themeIdParam)}" data-seller-id="${escapeHtmlAttr(sellerId)}" data-campaign-id="${escapeHtmlAttr(campaignId)}">
        ${submitting ? '<div class="widget-submitting-overlay"><span class="spinner"></span><span>Enviando sua inscrição...</span></div>' : ''}
        ${progressHtml}
        <h2>${escapeHtmlAttr(stepHeadingTitle)}</h2>
        ${currentStep !== STEPS.PAGAMENTO_EXEC && !showingAlreadyEnrolled && subtitle ? `<p class="widget-step-subtitle">${escapeHtmlAttr(subtitle)}</p>` : ''}
        ${checkoutErrorMessage ? `<div class="field-error" style="margin-bottom:1rem;line-height:1.4;">${escapeHtmlAttr(checkoutErrorMessage)}</div>` : ''}
        <div id="step-content"></div>
        ${actionsHtml}
      </div>`;

        const btnTermsAccept = shadow.getElementById('btn-terms-accept');
        if (btnTermsAccept) {
            btnTermsAccept.addEventListener('click', () => {
                formData.aceiteTermo = true;
                showTermsModal = false;
                termsScrolledToEnd = false;
                if (termsPdfObserver) {
                    termsPdfObserver.disconnect();
                    termsPdfObserver = null;
                }
                // Não avança automaticamente: fecha o modal e re-renderiza para
                // marcar o checkbox "Li e aceito" (read-only). O usuário precisa
                // clicar de novo no botão de avançar para prosseguir; nesse
                // segundo clique o goNext vê aceiteTermo=true e pula o modal.
                void render();
            });
        }

        const stepContent = shadow.getElementById('step-content');
        if (!stepContent) return;

        if (currentStep === STEPS.DADOS_BASICOS) {
            stepContent.innerHTML = renderDadosBasicosStep();
        } else if (currentStep === STEPS.ENDERECO) {
            stepContent.innerHTML = renderEnderecoStep();
        } else if (currentStep === STEPS.PLANO) {
            stepContent.innerHTML = renderPlanoStep();
            syncPlanFromFormData();

            // Checkbox de aceite é READ-ONLY: clicar no label/checkbox abre o
            // modal do termo. A marcação só acontece quando o aluno clica em
            // "SIM" no modal (que rola até o fim do PDF). Mantém o UX do
            // protótipo (checkbox visível + link) sem permitir aceite por
            // accident click sem ter lido o termo.
            const termosAceiteLabel = shadow.getElementById('termos-aceite-label');
            if (termosAceiteLabel && acceptanceTermPdfUrl) {
                termosAceiteLabel.addEventListener('click', (ev) => {
                    ev.preventDefault();
                    showTermsModal = true;
                    termsScrolledToEnd = false;
                    if (termsPdfObserver) {
                        termsPdfObserver.disconnect();
                        termsPdfObserver = null;
                    }
                    void render();
                    void initTermsPdf();
                });
            }
        } else if (currentStep === STEPS.PAGAMENTO_EXEC) {
            stepContent.innerHTML = renderCartaoStep();
        }

        const btnNext = shadow.getElementById('btn-next');
        if (btnNext) {
            btnNext.addEventListener('click', () => void goNext());
        }
        const btnBack = shadow.getElementById('btn-back');
        if (btnBack) btnBack.addEventListener('click', () => goBack());

        // No E4 do cartão usamos o mesmo "← Trocar forma de pagamento" do
        // PIX/Boleto (vive dentro do step-content, não no .actions) para
        // consistência visual com os outros métodos.
        const btnChangeCard = shadow.getElementById('btn-change-payment-method');
        if (btnChangeCard) btnChangeCard.addEventListener('click', () => goBack());

        attachListeners();
        requestAnimationFrame(restoreScroll);
    }

    async function initTermsPdf(): Promise<void> {
        const pagesEl = shadow.getElementById('terms-pdf-pages');
        const scrollEl = shadow.getElementById('terms-pdf-scroll');
        if (!pagesEl || !scrollEl || !acceptanceTermPdfUrl) return;

        const fullUrl = /^https?:\/\//i.test(acceptanceTermPdfUrl)
            ? acceptanceTermPdfUrl
            : joinApiBase(apiBase, acceptanceTermPdfUrl.replace(/^\//, ''));

        function enableButton(): void {
            const btn = shadow.getElementById('btn-terms-accept') as HTMLButtonElement | null;
            if (btn) btn.disabled = false;
            const hint = shadow.getElementById('terms-scroll-hint');
            if (hint) hint.style.display = 'none';
        }

        function observeLastPage(lastEl: Element): void {
            if (termsPdfObserver) termsPdfObserver.disconnect();

            // Sentinela de 1px após a última página — dispara assim que fica visível,
            // independentemente da altura do canvas vs. altura do container.
            const sentinel = document.createElement('div');
            sentinel.style.cssText = 'height:1px;width:100%;';
            lastEl.insertAdjacentElement('afterend', sentinel);

            termsPdfObserver = new IntersectionObserver(
                (entries) => {
                    if (entries.some((e) => e.isIntersecting)) {
                        termsScrolledToEnd = true;
                        termsPdfObserver?.disconnect();
                        termsPdfObserver = null;
                        enableButton();
                    }
                },
                { root: scrollEl, threshold: 0 },
            );
            termsPdfObserver.observe(sentinel);
        }

        try {
            await new Promise<void>((resolve, reject) => {
                if ((window as Record<string, unknown>).pdfjsLib) {
                    resolve();
                    return;
                }
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('pdfjs load failed'));
                document.head.appendChild(script);
            });

            const pdfjs = (window as any).pdfjsLib;
            pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

            const pdf = await pdfjs.getDocument(fullUrl).promise;

            const loadingEl = shadow.getElementById('terms-pdf-loading');
            if (loadingEl) loadingEl.remove();

            const containerWidth = pagesEl.getBoundingClientRect().width || 560;

            for (let p = 1; p <= pdf.numPages; p++) {
                const page = await pdf.getPage(p);
                const base = page.getViewport({ scale: 1 });
                const scale = Math.min((containerWidth - 8) / base.width, 2);
                const viewport = page.getViewport({ scale });

                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                canvas.style.cssText = 'display:block;width:100%;margin-bottom:4px;';

                await page.render({ canvasContext: canvas.getContext('2d') as any, viewport }).promise;
                pagesEl.appendChild(canvas);
            }

            const lastPage = pagesEl.lastElementChild;
            if (!lastPage) {
                termsScrolledToEnd = true;
                enableButton();
                return;
            }

            // PDF curto que já cabe sem rolar: habilita imediatamente
            if (scrollEl.scrollHeight <= scrollEl.clientHeight + 4) {
                termsScrolledToEnd = true;
                enableButton();
                return;
            }

            observeLastPage(lastPage);
        } catch {
            // Fallback: exibe iframe e libera botão após 30s
            const loadingEl = shadow.getElementById('terms-pdf-loading');
            if (loadingEl) {
                const iframe = document.createElement('iframe');
                iframe.src = fullUrl;
                iframe.style.cssText = 'width:100%;flex:1;border:none;min-height:420px;';
                iframe.title = 'Termo de aceite';
                loadingEl.replaceWith(iframe);
            }
            window.setTimeout(() => {
                termsScrolledToEnd = true;
                enableButton();
            }, 30000);
        }
    }

    await ensureStates();
    await trackStep();
    void render();
}
